import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { _Api } from "../common/api/_call";
import { error, success, loading, Updateloading } from "../common/helpers/toastify";
import { toFormatPrice } from "../common/helpers/function";
import currencyFormatter from "currency-formatter";
import CheckoutBlank from "../checkout/checkoutBlank";
import faromainicon from "../../assets/images/faromainicon.svg";
import camiconuploadimage from "../../assets/images/camiconuploadimage.svg";
import mikevoiceicon from "../../assets/images/mikevoiceicon.svg";
import greyfaroicon from "../../assets/images/greyfaroicon.svg";
import righttick from "../../assets/images/righttick.svg";
import whitetickicon from "../../assets/images/whitetickicon.svg";
import scanEyeIcon from "../../assets/images/scan-eye.svg";
import cardSearchIcon from "../../assets/images/newcatalogsearchicon.svg";
import shoppingbag from "../../assets/images/shoppingbag.svg";
import editPencilSvg from "../../assets/images/editPencilSvg.svg";
import reddeletebtnsvg from "../../assets/images/reddeletebtnsvg.svg";
import LensImageIcon from "../../assets/images/uploadimagePlaceholder.png";
import {
  buildFaroSessionSnapshot,
  clearFaroSession,
  loadFaroSession,
  resolveFaroScope,
  saveFaroSession,
  setFaroActiveScope,
  setFaroCustomOrderActive,
  FARO_SCOPES,
} from "./faroSession";
import "./SmartCatalogSearch.scss";

const SEARCH_ENDPOINT = "api/v1/POS/catalog/search";
const DISCOVER_ENDPOINT = "api/v1/POS/catalog/discover";
const ADD_INVENTORY_ITEMS_TO_CART_ENDPOINT =
  "api/v1/POS/customOrder/addInventoryItemsToCart";
const ADD_INVENTORY_ITEMS_TO_CUSTOM_ORDER_ENDPOINT =
  "api/v1/POS/customOrder/addInventoryItemsToCustomOrder";

const DEFAULT_FILTERS = {
  metal: "",
  type: "",
  maxPrice: "",
  inStock: false,
};

const SEARCH_EXAMPLES = [
  "yellow gold ring",
  "diamond stud earrings",
  "rose gold bracelet",
];
const DISCOVER_EXAMPLES = [
  "anniversary gift for my wife who loves classic diamonds",
  "elegant everyday necklace under 20000",
  "statement piece for a black-tie event",
];
const DISCOVER_PROMPT_GROUPS = [
  {
    key: "recipient",
    label: "Who is it for?",
    starter: "I'm shopping for...",
    children: [
      "Myself",
      "Wife",
      "Husband",
      "Fiancée",
      "Fiancé",
      "Girlfriend",
      "Boyfriend",
      "Mother",
      "Father",
      "Daughter",
      "Son",
      "Friend",
      "Client",
      "Bride",
      "Groom",
    ],
    makeSentence: (value) =>
      `I'm shopping for ${
        value === "Myself" ? "myself" : `my ${value.toLowerCase()}`
      }.`,
  },
  {
    key: "occasion",
    label: "Occasion",
    starter: "This jewelry is for...",
    children: [
      "Anniversary",
      "Engagement",
      "Wedding",
      "Birthday",
      "Graduation",
      "Mother's Day",
      "Valentine's",
      "Christmas",
      "Push Present",
      "Everyday",
      "Gift",
      "Celebration",
    ],
    makeSentence: (value) => `This jewelry is for ${value.toLowerCase()}.`,
  },
  {
    key: "jewelryType",
    label: "Jewelry Type",
    starter: "I'm looking for a...",
    children: ["Ring", "Necklace", "Pendant", "Earrings", "Bracelet", "Bangle", "Brooch", "Set"],
    makeSentence: (value) => `I'm looking for a ${value.toLowerCase()}.`,
  },
  {
    key: "metal",
    label: "Metal",
    starter: "I'd like it to be made of...",
    children: ["White Gold", "Yellow Gold", "Rose Gold", "Platinum", "Silver", "Two-Tone", "18K", "14K"],
    makeSentence: (value) => `I'd like it to be made of ${value.toLowerCase()}.`,
  },
  {
    key: "gemstone",
    label: "Gemstone",
    starter: "I'd like it to include...",
    children: [
      "Diamond",
      "Sapphire",
      "Emerald",
      "Ruby",
      "Pearl",
      "Opal",
      "Morganite",
      "Aquamarine",
      "Tanzanite",
      "Topaz",
      "No Gemstone",
    ],
    makeSentence: (value) => `I'd like it to include ${value.toLowerCase()}.`,
  },
  {
    key: "style",
    label: "Style",
    starter: "I prefer a... style.",
    children: ["Classic", "Timeless", "Minimal", "Modern", "Vintage", "Luxury", "Elegant", "Romantic", "Bold", "Statement", "Delicate", "Chic"],
    makeSentence: (value) => `I prefer a ${value.toLowerCase()} style.`,
  },
  {
    key: "budget",
    label: "Budget",
    starter: "My budget is...",
    children: ["<$500", "$500–1K", "$1K–3K", "$3K–5K", "$5K+", "Flexible"],
    makeSentence: (value) => `My budget is ${value}.`,
  },
  {
    key: "theme",
    label: "Theme",
    starter: "I'm looking for a design inspired by...",
    children: ["Floral", "Nature", "Celestial", "Heart", "Infinity", "Geometric", "Art Deco", "Vintage", "Ocean", "Butterfly", "Royal", "Contemporary"],
    makeSentence: (value) => `I'm looking for a design inspired by ${value.toLowerCase()}.`,
  },
  {
    key: "color",
    label: "Color",
    starter: "I'm looking for... tones.",
    children: ["White", "Yellow", "Rose", "Blue", "Green", "Pink", "Red", "Black", "Champagne", "Rainbow"],
    makeSentence: (value) => `I'm looking for ${value.toLowerCase()} tones.`,
  },
  {
    key: "size",
    label: "Size",
    starter: "The size is...",
    children: ["US 5", "US 6", "US 7", "US 8", "US 9", "Unknown", "Small", "Medium", "Large", "Adjustable"],
    makeSentence: (value) => `The size is ${value}.`,
  },
  {
    key: "lifestyle",
    label: "Wear/Lifestyle",
    starter: "It will mostly be worn...",
    children: ["Everyday", "Office", "Casual", "Formal", "Evening", "Wedding", "Party", "Travel"],
    makeSentence: (value) => `It will mostly be worn for ${value.toLowerCase()}.`,
  },
  {
    key: "avoid",
    label: "Avoid",
    starter: "I'd like to avoid...",
    children: ["Yellow Gold", "Large Stones", "Bold Designs", "Trendy", "Heavy", "Color Gems", "Halo", "Vintage", "Oversized", "High Maintenance"],
    makeSentence: (value) => `I'd like to avoid ${value.toLowerCase()}.`,
  },
];

// Build a `filters` object containing only the fields the user actually set,
// matching the API contract ("send only what's set").
const buildFilters = (filters) => {
  const applied = {};
  if (filters.metal.trim()) applied.metal = filters.metal.trim();
  if (filters.type.trim()) applied.type = filters.type.trim();
  if (filters.maxPrice !== "" && !Number.isNaN(Number(filters.maxPrice))) {
    applied.maxPrice = Number(filters.maxPrice);
  }
  if (filters.inStock) applied.inStock = true;
  return applied;
};

// Read a picked file into a base64 data URI (data:image/jpeg;base64,...).
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const DEEPGRAM_API_KEY = process.env.REACT_APP_DEEPGRAM_API_KEY;
const DEEPGRAM_WS_URL =
  "wss://api.deepgram.com/v1/listen?model=nova-2&smart_format=true&punctuate=true&interim_results=true&language=en";

const getSupportedMimeType = () => {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg"];
  return candidates.find(
    (type) =>
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported?.(type)
  );
};

/* -------------------------------------------------------------------------- */
/*  Inline icons                                                              */
/* -------------------------------------------------------------------------- */

const ImageGlyph = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden {...props}>
    <rect
      x="3"
      y="4"
      width="18"
      height="16"
      rx="2.5"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <circle cx="8.5" cy="9.5" r="1.6" fill="currentColor" />
    <path
      d="m4 17 4.5-4.5a1.5 1.5 0 0 1 2 0L16 18M14 15l1.8-1.8a1.5 1.5 0 0 1 2 0L20 15.4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Lens-style image upload popup: drag-drop + file picker */
const ImageUploadModal = ({ open, onClose, onFile, t }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsDragging(false);
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const acceptFile = (file) => {
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      return;
    }
    onFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer?.files?.[0];
    acceptFile(file);
  };

  return (
    <div
      className="scs__image-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="scs__image-modal"
        role="dialog"
        aria-modal="true"
        aria-label={t("Search any image with Lens")}
        onClick={(event) => event.stopPropagation()}
      >
       <div className="scs_upload_container">

        <h2 className="scs__image-modal-title">
          {t("Search any image with Lens")}
        </h2>
       <button
          type="button"
          className="scs__image-modal-close"
          aria-label={t("Close")}
          onClick={onClose}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path
              d="M5 15 15 5M5 5l10 10"
              stroke="#5F6368"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
       </div>

        <div
          className={`scs__image-modal-dropzone${
            isDragging ? " is-dragging" : ""
          }`}
          onDragEnter={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragging(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setIsDragging(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (event.currentTarget.contains(event.relatedTarget)) return;
            setIsDragging(false);
          }}
          onDrop={handleDrop}
        >
          <img src={LensImageIcon} alt="Lens Image" />
          <p className="scs__image-modal-hint">
            <span className="scs__image-modal-hint-text">{t("Drag an image here or")} </span>
            <button
              type="button"
              className="scs__image-modal-upload-link"
              onClick={() => fileInputRef.current?.click()}
            >
              {t("upload a file")}
            </button>
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              acceptFile(file);
            }}
          />
        </div>
      </div>
    </div>
  );
};

const pushGalleryImageUrl = (urls, seen, value) => {
  if (!value) return;
  if (typeof value === "string") {
    const url = value.trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    urls.push(url);
    return;
  }
  if (typeof value === "object") {
    pushGalleryImageUrl(
      urls,
      seen,
      value.image ||
        value.url ||
        value.original ||
        value.imageUrl ||
        value.main_image ||
        value.src ||
        value.thumb
    );
  }
};

const getProductGalleryImages = (product) => {
  if (!product) return [];
  const urls = [];
  const seen = new Set();
  const lists = [
    product.images,
    // product.imageUrls,
    // product.gallery,
    // product.main_image_Arr,
    product.main_images,
  ];
  lists.forEach((list) => {
    if (Array.isArray(list)) {
      list.forEach((item) => pushGalleryImageUrl(urls, seen, item));
    }
  });
  // pushGalleryImageUrl(urls, seen, product.imageUrl);
  pushGalleryImageUrl(urls, seen, product.main_image);
  pushGalleryImageUrl(urls, seen, product.image);
  // const variants = Array.isArray(product.variants) ? product.variants : [];
  // variants.forEach((variant) => {
  //   pushGalleryImageUrl(urls, seen, variant?.imageUrl);
  //   pushGalleryImageUrl(urls, seen, variant?.main_image);
  //   pushGalleryImageUrl(urls, seen, variant?.image);
  //   if (Array.isArray(variant?.images)) {
  //     variant.images.forEach((item) => pushGalleryImageUrl(urls, seen, item));
  //   }
  // });
  return urls;
};

const GalleryChevron = ({ direction }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    {direction === "prev" ? (
      <path
        d="M12.5 4.5 7 10l5.5 5.5"
        stroke="#9AA0A6"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M7.5 4.5 13 10l-5.5 5.5"
        stroke="#9AA0A6"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const ProductImageGalleryModal = ({ open, product, onClose, t }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mainFailed, setMainFailed] = useState(false);
  const images = open ? getProductGalleryImages(product) : [];
  const imageCount = images.length;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Reset only when the modal opens or the product changes — not on every parent re-render.
  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    setMainFailed(false);
  }, [open, product]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onCloseRef.current?.();
        return;
      }
      if (!imageCount) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + imageCount) % imageCount);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % imageCount);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, imageCount]);

  const safeIndex = imageCount
    ? Math.min(Math.max(activeIndex, 0), imageCount - 1)
    : 0;
  const activeUrl = images[safeIndex] || "";

  useEffect(() => {
    setMainFailed(false);
  }, [activeUrl]);

  if (!open || !product) return null;

  const titleParts = [product?.name, product?.sku || product?.SKU].filter(
    Boolean
  );
  const title = titleParts.join(" ") || t("Product images");
  const canNavigate = imageCount > 1;

  const goPrev = (event) => {
    event?.stopPropagation?.();
    if (!canNavigate) return;
    setActiveIndex((i) => (i - 1 + imageCount) % imageCount);
  };

  const goNext = (event) => {
    event?.stopPropagation?.();
    if (!canNavigate) return;
    setActiveIndex((i) => (i + 1) % imageCount);
  };

  return (
    <div
      className="scs__gallery-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="scs__gallery-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="scs__gallery-modal-head">
          <h2 className="scs__gallery-modal-title" title={title}>
            {title}
          </h2>
          <button
            type="button"
            className="scs__gallery-modal-close"
            aria-label={t("Close")}
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
              <path
                d="M5 15 15 5M5 5l10 10"
                stroke="#202124"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="scs__gallery-modal-stage">
          {canNavigate ? (
            <button
              type="button"
              className="scs__gallery-modal-nav scs__gallery-modal-nav--prev"
              aria-label={t("Previous image")}
              onClick={goPrev}
            >
              <GalleryChevron direction="prev" />
            </button>
          ) : null}

          <div className="scs__gallery-modal-main">
            {activeUrl && !mainFailed ? (
              <img
                key={activeUrl}
                src={activeUrl}
                alt={title}
                onError={() => setMainFailed(true)}
              />
            ) : (
              <div className="scs__gallery-modal-empty" aria-hidden>
                <ImageGlyph width="40" height="40" />
              </div>
            )}
          </div>

          {canNavigate ? (
            <button
              type="button"
              className="scs__gallery-modal-nav scs__gallery-modal-nav--next"
              aria-label={t("Next image")}
              onClick={goNext}
            >
              <GalleryChevron direction="next" />
            </button>
          ) : null}
        </div>

        {imageCount > 0 ? (
          <div className="scs__gallery-modal-thumbs" role="list">
            {images.map((url, index) => (
              <button
                key={`${url}-${index}`}
                type="button"
                role="listitem"
                className={`scs__gallery-modal-thumb${
                  index === safeIndex ? " is-active" : ""
                }`}
                aria-label={t("Image {{n}}", { n: index + 1 })}
                aria-current={index === safeIndex ? "true" : undefined}
                onClick={() => setActiveIndex(index)}
              >
                <img src={url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Voice waveform (recording UI)                                             */
/* -------------------------------------------------------------------------- */

const VOICE_WAVE_BAR_COUNT = 48;
const VOICE_WAVE_HISTORY_SIZE = 440;
const VOICE_WAVE_DOT_HEIGHT = 4;
const VOICE_WAVE_MAX_HEIGHT = 38;
const VOICE_WAVE_SAMPLE_MS = 45;

const createDotLineLevels = (count = VOICE_WAVE_BAR_COUNT) =>
  Array.from({ length: count }, () => VOICE_WAVE_DOT_HEIGHT);

const createVoiceHistory = () =>
  Array.from({ length: VOICE_WAVE_HISTORY_SIZE }, (_, index) => ({
    id: index,
    // Keep subtle variance so waveform reads as bars, not a flat line.
    height: VOICE_WAVE_DOT_HEIGHT + (index % 3 === 0 ? 2 : index % 2),
  }));

const sampleSpeakingCluster = (analyser, freqData, timeData) => {
  let sumSquares = 0;
  for (let i = 0; i < timeData.length; i += 1) {
    const sample = (timeData[i] - 128) / 128;
    sumSquares += sample * sample;
  }
  const rms = Math.sqrt(sumSquares / timeData.length);
  if (rms <= 0.018) {
    return [
      VOICE_WAVE_DOT_HEIGHT,
      VOICE_WAVE_DOT_HEIGHT + 1,
      VOICE_WAVE_DOT_HEIGHT + 2,
    ];
  }

  analyser.getByteFrequencyData(freqData);
  const cluster = [];
  for (let index = 0; index < 3; index += 1) {
    const start = Math.floor((index / 3) * freqData.length * 0.7);
    const end = Math.floor(((index + 1) / 3) * freqData.length * 0.7);
    let bandSum = 0;
    let bandCount = 0;
    for (let bin = start; bin < end; bin += 1) {
      bandSum += freqData[bin];
      bandCount += 1;
    }
    const bandAvg = bandCount ? bandSum / bandCount : 0;
    const normalized = bandAvg / 255;
    cluster.push(
      Math.max(
        VOICE_WAVE_DOT_HEIGHT,
        Math.min(
          VOICE_WAVE_MAX_HEIGHT,
          Math.round(VOICE_WAVE_DOT_HEIGHT + normalized * 27)
        )
      )
    );
  }
  return cluster;
};

const normalizeVoiceTranscript = (text) =>
  String(text || "")
    .replace(/[.]+/g, "")
    .replace(/\s+/g, " ")
    .trim();

const escapeRegExp = (value) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const VoiceWaveform = ({ samples }) => {
  const bars = samples?.length ? samples : createVoiceHistory();

  return (
    <div className="scs__voice-wave" aria-hidden>
      <div className="scs__voice-wave-track">
        {bars.map((bar) => (
          <span
            key={bar.id}
            className="scs__voice-wave-bar"
            style={{ height: `${bar.height}px` }}
          />
        ))}
      </div>
    </div>
  );
};

const SearchbarTextField = ({
  textareaRef,
  value,
  onChange,
  onKeyDown,
  placeholder,
  ghostSuffix = "",
  ghostAfter = "",
  showVoiceWave,
  voiceBarLevels,
  viewResetKey = 0,
  initialExpanded = true,
  truncateRequestKey = 0,
  onExpandedChange,
  onVisualExpandedChange,
}) => {
  // Truncate ↔ expand: click truncated field to expand; click outside to truncate (multi-line only)
  const [expanded, setExpanded] = useState(initialExpanded);
  const [isMultiLine, setIsMultiLine] = useState(false);
  const localRef = useRef(null);
  const measureRef = useRef(null);
  const ghostLayerRef = useRef(null);
  const justExpandedRef = useRef(false);

  const setRefs = (el) => {
    localRef.current = el;
    if (!textareaRef) return;
    if (typeof textareaRef === "function") textareaRef(el);
    else textareaRef.current = el;
  };

  const singleLineValue = String(value || "").replace(/\s+/g, " ").trimStart();
  const canTruncate = isMultiLine && Boolean((value || "").trim());
  // Multi-line: expand/truncate UI. Single-line: always show textarea, but `expanded`
  // still tracks active (click inside) vs inactive (click outside) for Discovery/arrow.
  const showExpanded = expanded || showVoiceWave || !canTruncate;
  const isActiveExpanded = canTruncate ? showExpanded : expanded;
  const showGhost =
    Boolean(ghostSuffix || ghostAfter) && showExpanded && !showVoiceWave;

  // Parent reset: Discovery/chips → expand first; Search submit → truncate first
  useEffect(() => {
    setExpanded(initialExpanded);
    // Only clear the expand guard when collapsing. Clearing it on expand lets the
    // leftover click (from truncated → expanded) immediately re-truncate.
    if (!initialExpanded) {
      justExpandedRef.current = false;
    }
  }, [viewResetKey, initialExpanded]);

  // Click outside search box → truncate (multi-line UI) / inactive (single-line)
  useEffect(() => {
    if (!truncateRequestKey) return;
    justExpandedRef.current = false;
    setExpanded(false);
    onExpandedChange?.(false);
  }, [truncateRequestKey]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: only react to truncate requests

  // Measure multi-line from real value only — ghost hover must not
  // flip expand/Discovery layout (that blinks chips under the cursor).
  useEffect(() => {
    const measure = measureRef.current;
    if (!measure) return;

    measure.value = value || "";
    measure.style.height = "21px";
    const nextIsMultiLine =
      Boolean((value || "").includes("\n")) || measure.scrollHeight > 22;
    setIsMultiLine(nextIsMultiLine);
  }, [value, viewResetKey]);

  useEffect(() => {
    onVisualExpandedChange?.(isActiveExpanded);
  }, [isActiveExpanded, onVisualExpandedChange]);

  // Field height follows committed text only — ghost is overlay, no layout jump
  useEffect(() => {
    const field = localRef.current;
    if (!field || showVoiceWave || !showExpanded) return;
    field.style.height = "21px";
    if ((value || "").trim()) {
      field.style.height = `${Math.min(Math.max(field.scrollHeight, 21), 118)}px`;
    }
    // Ghost layer must match textarea height (not the taller field min-height),
    // or a hover suffix that doesn't fit wraps and peeks on a fake second line.
    const ghost = ghostLayerRef.current;
    if (ghost) {
      ghost.style.height = field.style.height || "21px";
    }
  }, [value, showExpanded, showVoiceWave, showGhost, ghostSuffix, ghostAfter]);

  useEffect(() => {
    if (!showExpanded || showVoiceWave || !localRef.current) return;
    if (!justExpandedRef.current) return;
    const field = localRef.current;
    field.focus();
    const len = field.value.length;
    try {
      field.setSelectionRange(len, len);
    } catch (_) {
      /* ignore */
    }
    justExpandedRef.current = false;
  }, [showExpanded, showVoiceWave]);

  const expandText = (e) => {
    e?.preventDefault?.();
    justExpandedRef.current = true;
    setExpanded(true);
    onExpandedChange?.(true);
  };

  const handleExpandedClick = () => {
    if (justExpandedRef.current) {
      justExpandedRef.current = false;
      return;
    }
    // Truncate only when text is more than one line
    if (!canTruncate) return;
    setExpanded(false);
    onExpandedChange?.(false);
  };

  const handleExpandedFocus = () => {
    // Single-line: click/focus inside after outside-click → active again (like expand)
    if (!canTruncate && !expanded) {
      setExpanded(true);
      onExpandedChange?.(true);
    }
  };

  return (
    <div
      className={`scs__searchbar-field${
        showVoiceWave ? " scs__searchbar-field--voice-active" : ""
      }${showExpanded ? " scs__searchbar-field--expanded" : ""}${
        showGhost ? " scs__searchbar-field--ghost" : ""
      }`}
    >
      {/* Hidden mirror used only to detect multi-line overflow */}
      <textarea
        ref={measureRef}
        className="scs__searchbar-input--measure"
        rows={1}
        readOnly
        tabIndex={-1}
        aria-hidden
        value={value || ""}
      />
      {showVoiceWave ? <VoiceWaveform samples={voiceBarLevels} /> : null}
      {showGhost ? (
        <div
          ref={ghostLayerRef}
          className="scs__searchbar-ghost-layer"
          aria-hidden
        >
          <span
            className={`scs__searchbar-ghost-flow${
              isMultiLine ? "" : " scs__searchbar-ghost-flow--single"
            }`}
          >
            <span className="scs__searchbar-ghost-value">{value}</span>
            {ghostSuffix ? (
              <span className="scs__searchbar-ghost-suffix">{ghostSuffix}</span>
            ) : null}
            {ghostAfter ? (
              <span className="scs__searchbar-ghost-after">{ghostAfter}</span>
            ) : null}
          </span>
        </div>
      ) : null}
      {showExpanded ? (
        <textarea
          ref={setRefs}
          className={showVoiceWave ? "scs__searchbar-input--voice-hidden" : undefined}
          rows={1}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onClick={handleExpandedClick}
          onFocus={handleExpandedFocus}
          placeholder={placeholder}
          aria-hidden={showVoiceWave || undefined}
          tabIndex={showVoiceWave ? -1 : undefined}
        />
      ) : (
        <input
          type="text"
          className="scs__searchbar-input--truncated"
          value={singleLineValue}
          readOnly
          onClick={expandText}
          onFocus={expandText}
          placeholder={placeholder}
        />
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Mic (Deepgram live STT)                                                   */
/* -------------------------------------------------------------------------- */

const MicButton = ({ value, onChange, disabled, t, onStatusChange, onAudioLevels }) => {
  const [recording, setRecording] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const recorderRef = useRef(null);
  const socketRef = useRef(null);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const historyRef = useRef(createVoiceHistory());
  const lastSampleAtRef = useRef(0);
  const nextBarIdRef = useRef(VOICE_WAVE_HISTORY_SIZE);
  const baseTextRef = useRef("");
  const finalRef = useRef("");
  const interimRef = useRef("");
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
    if (!(value || "").trim()) {
      baseTextRef.current = "";
      finalRef.current = "";
      interimRef.current = "";
    }
  }, [value]);

  const resetTranscriptRefs = (nextValue = valueRef.current) => {
    baseTextRef.current = (nextValue || "").trim();
    finalRef.current = "";
    interimRef.current = "";
  };

  const stopAudioMonitor = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    historyRef.current = createVoiceHistory();
    lastSampleAtRef.current = 0;
    nextBarIdRef.current = VOICE_WAVE_HISTORY_SIZE;
    onAudioLevels?.(createVoiceHistory());
  };

  const startAudioMonitor = (stream) => {
    stopAudioMonitor();
    historyRef.current = createVoiceHistory();
    lastSampleAtRef.current = 0;
    nextBarIdRef.current = VOICE_WAVE_HISTORY_SIZE;
    onAudioLevels?.(createVoiceHistory());

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;

    const audioCtx = new AudioContextCtor();
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.82;

    const source = audioCtx.createMediaStreamSource(stream);
    source.connect(analyser);

    audioCtxRef.current = audioCtx;
    analyserRef.current = analyser;

    const freqData = new Uint8Array(analyser.frequencyBinCount);
    const timeData = new Uint8Array(analyser.fftSize);

    const tick = (timestamp = 0) => {
      if (!analyserRef.current) return;

      if (timestamp - lastSampleAtRef.current >= VOICE_WAVE_SAMPLE_MS) {
        lastSampleAtRef.current = timestamp;
        const nextHeights = sampleSpeakingCluster(analyser, freqData, timeData);
        const nextBars = nextHeights.map((height) => {
          nextBarIdRef.current += 1;
          return { id: nextBarIdRef.current, height };
        });
        historyRef.current = [...historyRef.current, ...nextBars].slice(
          -VOICE_WAVE_HISTORY_SIZE
        );
        onAudioLevels?.(historyRef.current);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    audioCtx.resume().catch(() => {});
    rafRef.current = requestAnimationFrame(tick);
  };

  const cleanup = () => {
    stopAudioMonitor();
    try {
      recorderRef.current?.state !== "inactive" && recorderRef.current?.stop();
    } catch (err) {
      /* no-op */
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }
    recorderRef.current = null;
    streamRef.current = null;
    socketRef.current = null;
  };

  const pushText = (interim) => {
    const next = [baseTextRef.current, finalRef.current, normalizeVoiceTranscript(interim)]
      .filter(Boolean)
      .join(" ");
    onChange(next);
  };

  const startRecording = async () => {
    if (!DEEPGRAM_API_KEY) {
      error(t("Speech-to-text key is not configured"));
      return;
    }
    if (
      !navigator.mediaDevices?.getUserMedia ||
      typeof MediaRecorder === "undefined"
    ) {
      error(t("Microphone is not supported in this browser"));
      return;
    }
    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      error(t("Audio recording is not supported in this browser"));
      return;
    }

    setConnecting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      resetTranscriptRefs(valueRef.current);

      const socket = new WebSocket(DEEPGRAM_WS_URL, ["token", DEEPGRAM_API_KEY]);
      socketRef.current = socket;

      socket.onopen = () => {
        const recorder = new MediaRecorder(stream, { mimeType });
        recorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0 && socket.readyState === WebSocket.OPEN) {
            socket.send(e.data);
          }
        };
        recorder.start(250);
        startAudioMonitor(stream);
        setConnecting(false);
        setRecording(true);
      };

      socket.onmessage = (msg) => {
        let data;
        try {
          data = JSON.parse(msg.data);
        } catch (err) {
          return;
        }
        const transcript = normalizeVoiceTranscript(
          data?.channel?.alternatives?.[0]?.transcript
        );
        if (!transcript) return;
        if (data.is_final) {
          finalRef.current = finalRef.current
            ? `${finalRef.current} ${transcript}`
            : transcript;
          interimRef.current = "";
          pushText("");
        } else {
          interimRef.current = transcript;
          pushText(transcript);
        }
      };

      socket.onerror = () => {
        error(t("Voice connection failed"));
        cleanup();
        setConnecting(false);
        setRecording(false);
      };

      socket.onclose = () => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
      };
    } catch (err) {
      error(t("Microphone access was denied"));
      cleanup();
      setConnecting(false);
      setRecording(false);
    }
  };

  const stopRecording = () => {
    try {
      if (recorderRef.current?.state !== "inactive") {
        recorderRef.current?.stop();
      }
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: "CloseStream" }));
        socketRef.current.close();
      }
    } catch (err) {
      /* no-op */
    }

    const pendingInterim = (interimRef.current || "").trim();
    if (pendingInterim) {
      finalRef.current = finalRef.current
        ? `${finalRef.current} ${pendingInterim}`
        : pendingInterim;
      interimRef.current = "";
      // Ensure right-check click commits spoken text into input.
      pushText("");
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    stopAudioMonitor();
    resetTranscriptRefs();
    setRecording(false);
  };

  const toggle = () => {
    if (connecting) return;
    if (recording) stopRecording();
    else startRecording();
  };

  useEffect(() => {
    onStatusChange?.({ recording, connecting });
  }, [recording, connecting, onStatusChange]);

  useEffect(
    () => () => {
      onStatusChange?.({ recording: false, connecting: false });
      cleanup();
    },
    [onStatusChange]
  );

  const className = `scs__mic${recording ? " scs__mic--confirm" : ""}${
    connecting ? " is-connecting" : ""
  }`;

  return (
    <button
      type="button"
      className={className}
      onClick={toggle}
      disabled={disabled || connecting}
      title={
        connecting
          ? t("Connecting…")
          : recording
            ? t("Done speaking")
            : t("Speak to search")
      }
      aria-label={recording ? t("Done speaking") : t("Voice input")}
      aria-pressed={recording}
    >
      {connecting ? (
        <span className="scs__mic-spinner" aria-hidden />
      ) : recording ? (
        <img src={righttick} alt="" aria-hidden />
      ) : (
        <img src={mikevoiceicon} alt="" aria-hidden />
      )}
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/*  Product card + skeleton                                                   */
/* -------------------------------------------------------------------------- */

const getCurrencySymbol = (currencyCode) => {
  try {
    const raw = localStorage.getItem("pos-loc");
    if (raw) {
      const pos = JSON.parse(raw);
      if (pos?.symbol) return pos.symbol;
    }
  } catch {
    /* ignore */
  }

  if (currencyCode) {
    const found = currencyFormatter.findCurrency(currencyCode);
    if (found?.symbol) return found.symbol;
  }

  try {
    const raw = localStorage.getItem("getPOSSettings");
    if (raw) {
      const settings = JSON.parse(raw);
      const code = settings?.price_format?.currency;
      if (code) {
        const found = currencyFormatter.findCurrency(code);
        return found?.symbol || code;
      }
    }
  } catch {
    /* ignore */
  }

  return "";
};

const formatCardPrice = (price) => {
  if (!price) return "—";

  if (price.amount != null && price.amount !== "") {
    return toFormatPrice(price.amount, { addSymbol: true });
  }

  const symbol = getCurrencySymbol(price.currency);
  const formatted = price.formatted || "";
  const label = [symbol, formatted].filter(Boolean).join(" ").trim();
  return label || "—";
};

const toOptionList = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      value: item?.value ?? item?._id ?? item?.id ?? "",
      label: item?.label ?? item?.name ?? "",
    }))
    .filter((item) => item.value && item.label);
};

const getProductAttributeOptions = (product, attribute) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const noVariants = variants.length === 0;

  // No variant rows — show mother product attributes from Faro /search payload.
  if (noVariants) {
    const motherRaw = product?.[`mother_${attribute}`];
    if (motherRaw != null && String(motherRaw).trim()) {
      const text = String(motherRaw).trim();
      return [{ value: text, label: text }];
    }
  }

  const listKey = `${attribute}s`;
  const fromList = toOptionList(product?.[listKey]);
  if (fromList.length) return fromList;

  const raw =
    product?.[attribute] ||
    product?.[`${attribute}Name`] ||
    product?.[`${attribute}_name`] ||
    "";
  if (!raw) return [];

  // Faro /search returns the singular attribute (e.g. product.metal) as an
  // {value, label} object rather than a plain string.
  if (typeof raw === "object") {
    const value = raw.value ?? raw._id ?? raw.id ?? "";
    const label = raw.label ?? raw.name ?? "";
    return value && label ? [{ value, label }] : [];
  }

  const text = String(raw).trim();
  return [{ value: text, label: text }];
};

const getVariantScore = (variant) => {
  const score = Number(variant?.score);
  return Number.isFinite(score) ? score : -Infinity;
};

const getHighestScoreVariant = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  if (!variants.length) return null;
  return variants.reduce((best, variant) =>
    getVariantScore(variant) > getVariantScore(best) ? variant : best
  );
};

const getDefaultSelections = (product) => {
  const metals = getProductAttributeOptions(product, "metal");
  const stones = getProductAttributeOptions(product, "stone");
  const sizes = getProductAttributeOptions(product, "size");
  const bestVariant = getHighestScoreVariant(product);

  if (bestVariant) {
    return {
      metal: bestVariant.metalid || metals[0]?.value || "",
      stone: bestVariant.stoneid || stones[0]?.value || "",
      size: bestVariant.sizeid || sizes[0]?.value || "",
    };
  }

  return {
    metal: metals[0]?.value || "",
    stone: stones[0]?.value || "",
    size: sizes[0]?.value || "",
  };
};

const findMatchingVariant = (product, selections) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const matches = variants.filter(
    (variant) =>
      variant?.metalid === selections.metal &&
      variant?.stoneid === selections.stone &&
      variant?.sizeid === selections.size
  );
  if (!matches.length) return null;
  return matches.reduce((best, variant) =>
    getVariantScore(variant) > getVariantScore(best) ? variant : best
  );
};

const getVariantImageUrl = (variant, product) => {
  const variantImage =
    variant?.imageUrl || variant?.main_image || variant?.image || "";
  return variantImage || product?.imageUrl || "";
};

const getSelectionLabel = (options, value) =>
  options.find((option) => option.value === value)?.label || "";

const getVariantCombinationId = (variant, product) => {
  const variantId = variant?.variant_id || variant?._id || variant?.id || "";
  if (variantId) return variantId;
  // No variant match / id — fall back to mother product id for cart APIs.
  return (
    product?._id ||
    product?.mother_product_id ||
    product?.mother_id ||
    ""
  );
};

const buildOrderItem = ({ product, selections, inStock = false }) => {
  const metals = getProductAttributeOptions(product, "metal");
  const stones = getProductAttributeOptions(product, "stone");
  const sizes = getProductAttributeOptions(product, "size");
  const matchedVariant = findMatchingVariant(product, selections);
  const variantCombinationId = getVariantCombinationId(matchedVariant, product);

  return {
    id: `${product._id || product.sku || "item"}-${Date.now()}`,
    product,
    selections,
    matchedVariant,
    variantCombinationId,
    inStock: Boolean(inStock),
    name: product?.name || "—",
    imageUrl: getVariantImageUrl(matchedVariant, product),
    sku: matchedVariant?.SKU || product?.sku || "—",
    price: product?.price,
    metalLabel: getSelectionLabel(metals, selections.metal),
    stoneLabel: getSelectionLabel(stones, selections.stone),
    sizeLabel: getSelectionLabel(sizes, selections.size),
  };
};

const getProductCardKey = (product, index = 0) =>
  product._id || product.sku || product.stock_id || index;

const getProductIdentity = (product) =>
  product?.stock_id || product?._id || product?.sku || "";

const getOrderItemIdentity = (item) =>
  getProductIdentity(item?.product) || item?.sku || "";

const buildAddedProductCounts = (items) => {
  const counts = {};
  (Array.isArray(items) ? items : []).forEach((item) => {
    const key = getOrderItemIdentity(item);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

const getMotherSku = (product) =>
  product?.mother_SKU || product?.SKU || product?.sku || "";

const ConfigSelect = ({
  id,
  label,
  value,
  options,
  onChange,
  t,
  isOpen,
  onToggle,
  onClose,
  readOnly = false,
  hideLabel = false,
}) => {
  const wrapRef = useRef(null);
  const selectedLabel =
    options.find((option) => option.value === value)?.label ||
    value ||
    t(label);
  const hasValue = Boolean(value);
  const isLocked = readOnly || !options.length;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapRef.current?.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, onClose]);

  return (
    <div className={`scs__card-config-field${hideLabel ? " scs__card-config-field--compact" : ""}`}>
      {hideLabel ? null : (
        <label className="scs__card-config-label" htmlFor={id}>
          {t(label)}
        </label>
      )}
      <div className="scs__card-config-select-wrap" ref={wrapRef}>
        <button
          type="button"
          id={id}
          className={`scs__card-config-select${
            isOpen ? " scs__card-config-select--open" : ""
          }${hasValue ? " scs__card-config-select--filled" : ""}${
            readOnly ? " scs__card-config-select--readonly" : ""
          }`}
          onClick={isLocked ? undefined : onToggle}
          disabled={isLocked}
          aria-label={hideLabel ? t(label) : undefined}
          aria-haspopup={readOnly ? undefined : "listbox"}
          aria-expanded={readOnly ? undefined : isOpen}
        >
          <span className="scs__card-config-select-value">{selectedLabel}</span>
          {!readOnly ? (
            <span className="scs__card-config-select-chevron" aria-hidden />
          ) : null}
        </button>

        {!readOnly && isOpen && options.length > 0 ? (
          <div className="scs__card-config-dropdown" role="listbox">
            <div className="scs__card-config-dropdown-scroll">
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={value === option.value}
                  className={`scs__card-config-dropdown-item${
                    value === option.value
                      ? " scs__card-config-dropdown-item--active"
                      : ""
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    onClose();
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const TOAST_DURATION_MS = 3000;

const ItemAddedToast = ({ message }) => (
  <div className="scs__toast" role="status" aria-live="polite">
    <div className="scs__toast-body">
      <span className="scs__toast-icon" aria-hidden>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="14" fill="#00B998" />
          <path
            d="M10.5 16.25L14.25 20L21.5 12.75"
            stroke="#FFFFFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="scs__toast-text">{message}</span>
    </div>
    <span
      className="scs__toast-progress"
      style={{ animationDuration: `${TOAST_DURATION_MS}ms` }}
      aria-hidden
    />
  </div>
);

const showItemAddedToast = (message) => {
  const toastId = "scs-item-added";
  const content = <ItemAddedToast message={message} />;
  const options = {
    toastId,
    position: "top-right",
    autoClose: TOAST_DURATION_MS,
    hideProgressBar: true,
    closeButton: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    className: "scs__toast-host",
    bodyClassName: "scs__toast-host-body",
  };

  // Keep a single toast — refresh it instead of stacking
  if (toast.isActive(toastId)) {
    toast.update(toastId, {
      render: content,
      autoClose: TOAST_DURATION_MS,
    });
    return;
  }

  toast(content, options);
};

const FloatingSearchButton = ({ count, t, onClick }) => {
  if (!count) return null;

  return (
    <button
      type="button"
      className="scs__floating-search"
      aria-label={t("Selected items: {{count}}", { count })}
      onClick={onClick}
    >
      <img className="scs__floating-search-icon" src={shoppingbag} alt="" aria-hidden />
      <span className="scs__floating-search-badge" aria-hidden>
        {count}
      </span>
    </button>
  );
};

const OrderItemCard = ({
  item,
  t,
  isEditing,
  openDropdown,
  onToggleDropdown,
  onCloseDropdown,
  onEdit,
  onDelete,
  onUpdateSelections,
  onConfirmEdit,
  showEdit = true,
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const product = item.product;
  const metalOptions = getProductAttributeOptions(product, "metal");
  const stoneOptions = getProductAttributeOptions(product, "stone");
  const sizeOptions = getProductAttributeOptions(product, "size");
  const attributeParts = [item.metalLabel, item.stoneLabel, item.sizeLabel].filter(
    Boolean
  );
  const priceLabel = formatCardPrice(item.price);
  const itemKey = item.id;
  const editingMatchedVariant = findMatchingVariant(product, item.selections);
  const editingImageUrl = getVariantImageUrl(editingMatchedVariant, product);

  useEffect(() => {
    setImgFailed(false);
  }, [item.imageUrl, editingImageUrl]);

  const updateSelection = (key) => (value) => {
    onUpdateSelections(item.id, { ...item.selections, [key]: value });
  };

  if (isEditing) {
    return (
      <article className="scs__order-item scs__order-item--editing">
        <div className="scs__order-item-box scs__order-item-box--config">
          <div className="scs__order-item-config-wrap">
            <div className="scs__card-config-head">
              <div className="scs__card-config-thumb">
                {editingImageUrl && !imgFailed ? (
                  <img
                    src={editingImageUrl}
                    alt={item.name}
                    loading="lazy"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <div className="scs__card-media-empty" aria-hidden>
                    <ImageGlyph width="20" height="20" />
                  </div>
                )}
              </div>

              <div className="scs__card-config-copy">
                <h3 className="scs__card-config-name" title={item.name}>
                  {item.name}
                </h3>
                <span className="scs__card-config-sku">{item.sku}</span>
              </div>

              <button
                type="button"
                className="scs__card-config-close"
                aria-label={t("Close")}
                onClick={() => onEdit(item)}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                  <path
                    d="M5 5L15 15M15 5L5 15"
                    stroke="#606060"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="scs__card-config-fields">
              <ConfigSelect
                id={`${itemKey}-metal`}
                label="Metal"
                value={item.selections.metal}
                options={metalOptions}
                onChange={updateSelection("metal")}
                t={t}
                isOpen={openDropdown === `${itemKey}-metal`}
                onToggle={() => onToggleDropdown(`${itemKey}-metal`)}
                onClose={onCloseDropdown}
              />
              <ConfigSelect
                id={`${itemKey}-stone`}
                label="Stone"
                value={item.selections.stone}
                options={stoneOptions}
                onChange={updateSelection("stone")}
                t={t}
                isOpen={openDropdown === `${itemKey}-stone`}
                onToggle={() => onToggleDropdown(`${itemKey}-stone`)}
                onClose={onCloseDropdown}
              />
              <ConfigSelect
                id={`${itemKey}-size`}
                label="Size"
                value={item.selections.size}
                options={sizeOptions}
                onChange={updateSelection("size")}
                t={t}
                isOpen={openDropdown === `${itemKey}-size`}
                onToggle={() => onToggleDropdown(`${itemKey}-size`)}
                onClose={onCloseDropdown}
              />
            </div>

            <button
              type="button"
              className="scs__card-confirm"
              aria-label={t("Confirm selection")}
              onClick={() => onConfirmEdit(item.id)}
            >
              <img
                className="scs__card-confirm-icon scs__card-confirm-icon--default"
                src={righttick}
                alt=""
                aria-hidden
              />
              <img
                className="scs__card-confirm-icon scs__card-confirm-icon--hover"
                src={whitetickicon}
                alt=""
                aria-hidden
              />
            </button>
            {/* <div aria-label={t("Confirm selection")}
              onClick={() => onConfirmEdit(item.id)}>
              <span>Add to Order</span>
            </div> */}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="scs__order-item">
      <div className="scs__order-item-box">
        <div className="scs__order-item-view">
          <div className="scs__order-item-media">
            {item.imageUrl && !imgFailed ? (
              <img
                src={item.imageUrl}
                alt={item.name}
                loading="lazy"
                onError={() => setImgFailed(true)}
              />
            ) : (
              <div className="scs__order-item-media-empty" aria-hidden>
                <ImageGlyph width="26" height="26" />
              </div>
            )}
          </div>

          <div className="scs__order-item-body">
            <div className="scs__order-item-copy">
              <div className="scs__order-item-title-wrap">
                <h3 className="scs__order-item-name" title={item.name}>
                  {item.name}
                </h3>
              </div>

              <div className="scs__order-item-meta">
                <span className="scs__order-item-sku">{item.sku}</span>
                {/* {attributeParts.length ? (
                  <div className="scs__order-item-attrs">
                    {attributeParts.map((part, index) => (
                      <React.Fragment key={`${itemKey}-attr-${part}`}>
                        {index > 0 ? (
                          <span className="scs__order-item-attr-sep" aria-hidden>
                            ·
                          </span>
                        ) : null}
                        <span className="scs__order-item-attr">{part}</span>
                      </React.Fragment>
                    ))}
                  </div>
                ) : null} */}
              </div>
            </div>

            <div className="scs__order-item-footer">
              <span className="scs__order-item-price">{priceLabel}</span>
              <div
                className={`scs__order-item-actions${
                  showEdit ? "" : " scs__order-item-actions--delete-only"
                }`}
              >
                {showEdit ? (
                  <button
                    type="button"
                    className="scs__order-item-action-btn"
                    aria-label={t("Edit item")}
                    onClick={() => onEdit(item)}
                  >
                    <img src={editPencilSvg} alt="" aria-hidden />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="scs__order-item-action-btn scs__order-item-action-btn--delete"
                  aria-label={t("Delete item")}
                  onClick={() => onDelete(item.id)}
                >
                  <img src={reddeletebtnsvg} alt="" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const OrderPanel = ({
  items,
  t,
  editingItemId,
  openDropdown,
  onToggleDropdown,
  onCloseDropdown,
  onClose,
  onEdit,
  onDelete,
  onUpdateSelections,
  onConfirmEdit,
  onSubmit,
  isSubmitting = false,
  showOrderEdit = true,
}) => {
  const itemLabel =
    items.length === 1
      ? t("{{count}} Item", { count: items.length })
      : t("{{count}} Items", { count: items.length });

  return (
    <section className="scs__order-panel" aria-label={t("Order")}>
      <header className="scs__order-head">
        <h2 className="scs__order-title">{t("Order")}</h2>
        <button
          type="button"
          className="scs__order-close"
          aria-label={t("Close")}
          onClick={onClose}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
            <path
              d="M5 5L15 15M15 5L5 15"
              stroke="#606060"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className="scs__order-toolbar">
        <span className="scs__order-count">{itemLabel}</span>
        <button
          type="button"
          className={`scs__order-submit${
            isSubmitting ? " scs__order-submit--loading" : ""
          }`}
          onClick={onSubmit}
          disabled={isSubmitting || !items.length}
          aria-busy={isSubmitting || undefined}
        >
          {isSubmitting ? (
            <>
              <span className="scs__order-submit-spinner" aria-hidden />
              <span>{t("Confirm")}</span>
            </>
          ) : (
            t("Confirm")
          )}
        </button>
      </div>

      <div className="scs__order-list">
        {items.map((item) => (
          <OrderItemCard
            key={item.id}
            item={item}
            t={t}
            isEditing={editingItemId === item.id}
            openDropdown={openDropdown}
            onToggleDropdown={onToggleDropdown}
            onCloseDropdown={onCloseDropdown}
            onEdit={onEdit}
            onDelete={onDelete}
            onUpdateSelections={onUpdateSelections}
            onConfirmEdit={onConfirmEdit}
            showEdit={showOrderEdit}
          />
        ))}
      </div>
    </section>
  );
};

const InStockProductCard = ({
  product,
  t,
  index = 0,
  addedCount = 0,
  onConfirmSelection,
  onImageClick,
  isConfigOpen = false,
  onOpenConfig,
  onCloseConfig,
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const [selections] = useState(() => getDefaultSelections(product));

  const metalOptions = getProductAttributeOptions(product, "metal");
  const stoneOptions = getProductAttributeOptions(product, "stone");
  const sizeOptions = getProductAttributeOptions(product, "size");
  const matchedVariant = findMatchingVariant(product, selections);
  const priceLabel = formatCardPrice(product?.price);
  const displaySku = matchedVariant?.SKU || product?.sku || "—";
  const displayStockId = product?.stock_id || "—";
  const cardKey = getProductCardKey(product, index);
  const metalLabel = getSelectionLabel(metalOptions, selections.metal) || "—";
  const stoneLabel = getSelectionLabel(stoneOptions, selections.stone) || "—";
  const sizeLabel = getSelectionLabel(sizeOptions, selections.size) || "—";

  const openPreview = (event) => {
    event?.stopPropagation?.();
    onOpenConfig?.(cardKey);
  };

  const closePreview = () => {
    onCloseConfig?.();
  };

  return (
    <article
      className={`scs__card scs__card--in${
        isConfigOpen ? " scs__card--preview" : ""
      }`}
      style={{ animationDelay: `${Math.min(index * 45, 360)}ms` }}
      onMouseLeave={closePreview}
    >
      <div className="scs__card-box">
        {isConfigOpen ? (
          <div className="scs__card-detail">
            <div className="scs__card-detail-top">
              <div className="scs__card-detail-thumb">
                {product?.imageUrl && !imgFailed ? (
                  <img
                    src={product.imageUrl}
                    alt={product?.name || product?.sku || ""}
                    loading="lazy"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <div className="scs__card-media-empty" aria-hidden>
                    <ImageGlyph width="20" height="20" />
                  </div>
                )}
              </div>

              <div className="scs__card-detail-copy">
                <h3 className="scs__card-detail-name">
                  {product?.name || "—"}
                </h3>
                <span className="scs__card-detail-sku">{displaySku}</span>
              </div>
            </div>

            <div className="scs__card-detail-divider" aria-hidden />

            <div className="scs__card-detail-attrs">
              <div className="scs__card-detail-attr">
                <span className="scs__card-detail-label">{t("Metal")}</span>
                <span className="scs__card-detail-value">{metalLabel}</span>
              </div>
              <div className="scs__card-detail-attr">
                <span className="scs__card-detail-label">{t("Stone")}</span>
                <span className="scs__card-detail-value">{stoneLabel}</span>
              </div>
              <div className="scs__card-detail-attr">
                <span className="scs__card-detail-label">{t("Size")}</span>
                <span className="scs__card-detail-value">{sizeLabel}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="scs__card-content">
            {addedCount > 0 ? (
              <span
                className="scs__card-added"
                aria-label={t("Added to order: {{count}}", { count: addedCount })}
              >
                <span aria-hidden>{addedCount}</span>
              </span>
            ) : null}

            <div className="scs__card-image-wrap">
              <button
                type="button"
                className="scs__card-media scs__card-media--clickable"
                aria-label={t("View product images")}
                onClick={() => onImageClick?.(product)}
              >
                {product?.imageUrl && !imgFailed ? (
                  <img
                    src={product.imageUrl}
                    alt={product?.name || product?.sku || ""}
                    loading="lazy"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <div className="scs__card-media-empty" aria-hidden>
                    <ImageGlyph width="26" height="26" />
                  </div>
                )}
              </button>
            </div>

            <div className="scs__card-body">
              <div className="scs__card-copy">
                <h3 className="scs__card-name" title={product?.name}>
                  {product?.name || "—"}
                </h3>
                <span className="scs__card-stock-id">{displayStockId}</span>
                <span className="scs__card-sku">{product?.sku || "—"}</span>
              </div>

              <div className="scs__card-footer">
                <span className="scs__card-price">{priceLabel}</span>
              </div>
            </div>

            <button
              type="button"
              className="scs__card-eye"
              aria-label={t("View product details")}
              onClick={openPreview}
            >
              <img src={cardSearchIcon} alt="" aria-hidden />
            </button>

            <button
              type="button"
              className="scs__card-add"
              aria-label={t("Add product")}
              onClick={() =>
                onConfirmSelection?.({
                  product,
                  selections: getDefaultSelections(product),
                })
              }
            >
              <span aria-hidden />
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

const CatalogProductCard = ({
  product,
  t,
  index = 0,
  addedCount = 0,
  onConfirmSelection,
  showStockId = false,
  fromQuickview = false,
  onImageClick,
  isConfigOpen = false,
  onOpenConfig,
  onCloseConfig,
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selections, setSelections] = useState(() => getDefaultSelections(product));

  const metalOptions = getProductAttributeOptions(product, "metal");
  const stoneOptions = getProductAttributeOptions(product, "stone");
  const sizeOptions = getProductAttributeOptions(product, "size");
  const matchedVariant = findMatchingVariant(product, selections);
  const priceLabel = formatCardPrice(product?.price);
  const displaySku = matchedVariant?.SKU || product?.sku || "—";
  const displayStockId = product?.stock_id || "—";
  const displayImageUrl = getVariantImageUrl(matchedVariant, product);
  const cardKey = getProductCardKey(product, index);

  useEffect(() => {
    setImgFailed(false);
  }, [displayImageUrl]);

  useEffect(() => {
    if (!isConfigOpen) {
      setOpenDropdown(null);
      return;
    }
    setSelections(getDefaultSelections(product));
    setOpenDropdown(null);
  }, [isConfigOpen, product]);

  const openConfigMode = () => {
    onOpenConfig?.(cardKey);
  };

  const closeConfigMode = () => {
    setOpenDropdown(null);
    onCloseConfig?.();
  };

  const handleAddClick = () => {
    // Quickview Faro: plus → load product in Quickview directly (no detail / Faro cart)
    if (fromQuickview) {
      onConfirmSelection?.({
        product,
        selections: getDefaultSelections(product),
      });
      return;
    }
    openConfigMode();
  };

  const handleConfirm = () => {
    onConfirmSelection?.({ product, selections });
    onCloseConfig?.();
  };

  const updateSelection = (key) => (value) => {
    setSelections((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <article
      className={`scs__card scs__card--in${isConfigOpen ? " scs__card--config" : ""}`}
      style={{ animationDelay: `${Math.min(index * 45, 360)}ms` }}
    >
      <div className="scs__card-box">
        {isConfigOpen ? (
          <div className="scs__card-config">
            <div className="scs__card-config-head">
              <div className="scs__card-config-thumb">
                {displayImageUrl && !imgFailed ? (
                  <img
                    src={displayImageUrl}
                    alt={product?.name || product?.sku || ""}
                    loading="lazy"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <div className="scs__card-media-empty" aria-hidden>
                    <ImageGlyph width="20" height="20" />
                  </div>
                )}
              </div>

              <div className="scs__card-config-copy">
                <h3 className="scs__card-config-name" title={product?.name}>
                  {product?.name || "—"}
                </h3>
                {showStockId ? (
                  <span className="scs__card-config-stock-id">{displayStockId}</span>
                ) : null}
                <span className="scs__card-config-sku">{displaySku}</span>
              </div>

              <button
                type="button"
                className="scs__card-config-close"
                aria-label={t("Close")}
                onClick={closeConfigMode}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
                  <path
                    d="M5 5L15 15M15 5L5 15"
                    stroke="#606060"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="scs__card-config-fields">
              <ConfigSelect
                id={`${cardKey}-metal`}
                label="Metal"
                value={selections.metal}
                options={metalOptions}
                onChange={updateSelection("metal")}
                t={t}
                isOpen={openDropdown === "metal"}
                onToggle={() =>
                  setOpenDropdown((current) => (current === "metal" ? null : "metal"))
                }
                onClose={() => setOpenDropdown(null)}
                hideLabel={true}
              />
              <ConfigSelect
                id={`${cardKey}-stone`}
                label="Stone"
                value={selections.stone}
                options={stoneOptions}
                onChange={updateSelection("stone")}
                t={t}
                isOpen={openDropdown === "stone"}
                onToggle={() =>
                  setOpenDropdown((current) => (current === "stone" ? null : "stone"))
                }
                onClose={() => setOpenDropdown(null)}
                hideLabel={true}
              />
              <ConfigSelect
                id={`${cardKey}-size`}
                label="Size"
                value={selections.size}
                options={sizeOptions}
                onChange={updateSelection("size")}
                t={t}
                isOpen={openDropdown === "size"}
                onToggle={() =>
                  setOpenDropdown((current) => (current === "size" ? null : "size"))
                }
                onClose={() => setOpenDropdown(null)}
                hideLabel={true}
              />
                <div className="add_to_order_button"  
              aria-label={t("Confirm selection")}
              onClick={handleConfirm}>
              <span>Add to Order</span>
            </div>
            </div>

            {/* <button
              type="button"
              className="scs__card-confirm"
              aria-label={t("Confirm selection")}
              onClick={handleConfirm}
            >
              <img
                className="scs__card-confirm-icon scs__card-confirm-icon--default"
                src={righttick}
                alt=""
                aria-hidden
              />
              <img
                className="scs__card-confirm-icon scs__card-confirm-icon--hover"
                src={whitetickicon}
                alt=""
                aria-hidden
              />
            </button> */}
          
          </div>
        ) : (
          <div className="scs__card-content">
            {addedCount > 0 ? (
              <span
                className="scs__card-added"
                aria-label={t("Added to order: {{count}}", { count: addedCount })}
              >
                <span aria-hidden>{addedCount}</span>
              </span>
            ) : null}

            <div className="scs__card-image-wrap">
              <button
                type="button"
                className="scs__card-media scs__card-media--clickable"
                aria-label={t("View product images")}
                onClick={() => onImageClick?.(product)}
              >
                {displayImageUrl && !imgFailed ? (
                  <img
                    src={displayImageUrl}
                    alt={product?.name || product?.sku || ""}
                    loading="lazy"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <div className="scs__card-media-empty" aria-hidden>
                    <ImageGlyph width="26" height="26" />
                  </div>
                )}
              </button>
            </div>

            <div className="scs__card-body">
              <div className="scs__card-copy">
                <h3 className="scs__card-name" title={product?.name}>
                  {product?.name || "—"}
                </h3>
                {showStockId ? (
                  <span className="scs__card-stock-id">{displayStockId}</span>
                ) : null}
                <span className="scs__card-sku">{product?.sku || "—"}</span>
              </div>

              <div className="scs__card-footer">
                <span className="scs__card-price">{priceLabel}</span>
              </div>
            </div>

            {(!showStockId || addedCount === 0 || fromQuickview) ? (
              <button
                type="button"
                className="scs__card-add"
                aria-label={t("Add product")}
                onClick={handleAddClick}
              >
                <span aria-hidden />
              </button>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
};

const ProductCard = ({ fromPosscreen = false, showStockId = false, ...props }) =>
  fromPosscreen ? (
    <InStockProductCard {...props} />
  ) : (
    <CatalogProductCard {...props} showStockId={showStockId} />
  );

const SkeletonCard = () => (
  <div className="scs__skeleton-card" aria-hidden>
    <span className="scs__skeleton-wave" />
  </div>
);

/* -------------------------------------------------------------------------- */
/*  Screen                                                                     */
/* -------------------------------------------------------------------------- */

const SmartCatalogSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state: routeState } = useLocation();
  const isFromPosscreen = Boolean(routeState?.fromPosscreen);
  const isFromQuickview = Boolean(routeState?.fromQuickview);
  const isFromProducts = Boolean(routeState?.fromProducts);
  // Separate Faro UI state per entry screen (posscreen / products / quickview / menu)
  const faroScope = resolveFaroScope(routeState);
  // Products page: isCrown true = catalog, false = inventory
  const isProductsInventory = Boolean(
    isFromProducts &&
      (routeState?.inStock || routeState?.filtertype === false)
  );
  // POS inventory cart flow (posscreen or products inventory)
  const isInventoryPosCart = Boolean(isFromPosscreen || isProductsInventory);
  const customer = useSelector((state) => state.app.customer);
  const saleperson = useSelector((state) => state.app.saleperson);

  const [mode, setMode] = useState("search"); // "search" | "discover"

  const [queryText, setQueryText] = useState("");
  const [imageData, setImageData] = useState("");
  const [imageName, setImageName] = useState("");
  const [distill] = useState(true);

  const [intent, setIntent] = useState("");

  const [filters, setFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    // POS inventory / Create Your Own / Products inventory → send filters.inStock
    inStock: Boolean(
      routeState?.fromPosscreen ||
        routeState?.inStock ||
        routeState?.filtertype === false
    ),
  }));
  const [limit] = useState(20);

  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [products, setProducts] = useState([]);
  const [weak, setWeak] = useState(false);
  const [activePromptKey, setActivePromptKey] = useState("");
  const [hoveredPromptLabel, setHoveredPromptLabel] = useState("");
  // Child-chip being hovered (for ghost preview)
  const [hoveredChildKey, setHoveredChildKey] = useState("");
  // Parent-chip hover ghost when field already has text (starter preview)
  const [hoveredParentGhost, setHoveredParentGhost] = useState("");
  // Debounce chip mouseleave so corner flicker does not blink ghost on/off
  const chipHoverLeaveTimerRef = useRef(null);
  const [selectedPromptChildren, setSelectedPromptChildren] = useState({});
  const [orderItems, setOrderItems] = useState([]);
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  // POS screen: products already confirmed into POS cart (kept until checkout)
  const [posConfirmedCount, setPosConfirmedCount] = useState(0);
  const [posConfirmedProductCounts, setPosConfirmedProductCounts] = useState({});
  const [editingOrderItemId, setEditingOrderItemId] = useState(null);
  const [orderOpenDropdown, setOrderOpenDropdown] = useState(null);
  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [restoreRequest, setRestoreRequest] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isImagePreviewExpanded, setIsImagePreviewExpanded] = useState(true);
  const [isImageUploadOpen, setIsImageUploadOpen] = useState(false);
  const [galleryProduct, setGalleryProduct] = useState(null);
  const [activeConfigCardKey, setActiveConfigCardKey] = useState(null);
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);
  const [voiceBarLevels, setVoiceBarLevels] = useState(null);
  const [searchViewResetKey, setSearchViewResetKey] = useState(0);
  const [searchStartExpanded, setSearchStartExpanded] = useState(true);
  const [searchTruncateKey, setSearchTruncateKey] = useState(0);
  const [searchAccentBorder, setSearchAccentBorder] = useState(false);
  const [isSearchFieldExpanded, setIsSearchFieldExpanded] = useState(true);
  // Discover results exit: 0 normal, 1 after 1st Discovery clear (X+mic), 2 after X (camera+Discovery)
  const [discoverExitStep, setDiscoverExitStep] = useState(0);
  const searchTextRef = useRef(null);
  const searchbarRef = useRef(null);
  const closeNavTimeoutRef = useRef(null);
  const resultsListScrollTopRef = useRef(0);
  // Exact intent last written by a prompt/child chip click; used to detect manual edits.
  const lastChipIntentRef = useRef("");
  // Previous discover text — used so only the edited child's chip deselects.
  const prevDiscoverIntentRef = useRef("");
  // After successful schedule/checkout, skip persist so stale search is not re-saved.
  const skipFaroPersistRef = useRef(false);
  const selectedPromptChildrenRef = useRef({});
  const activePromptKeyRef = useRef("");
  // After a child was selected for a group, keep parent via remnant (not exact starter)
  // until remnant is cleared — avoids parent dropping on the next keystroke after child.
  const promptHadChildrenRef = useRef({});
  selectedPromptChildrenRef.current = selectedPromptChildren;
  activePromptKeyRef.current = activePromptKey;
  const confirmedCount = orderItems.length;
  const cartBadgeCount =
    confirmedCount > 0
      ? confirmedCount
      : isFromPosscreen
        ? posConfirmedCount
        : 0;
  const addedProductCounts = useMemo(() => {
    const fromOrder = buildAddedProductCounts(orderItems);
    if (!isFromPosscreen) return fromOrder;
    const merged = { ...posConfirmedProductCounts };
    Object.entries(fromOrder).forEach(([id, count]) => {
      merged[id] = (merged[id] || 0) + count;
    });
    return merged;
  }, [orderItems, isFromPosscreen, posConfirmedProductCounts]);

  const handleVoiceStatusChange = useCallback(({ recording }) => {
    setIsVoiceRecording(Boolean(recording));
    if (!recording) {
      setVoiceBarLevels(null);
    }
  }, []);

  const handleVoiceAudioLevels = useCallback((levels) => {
    setVoiceBarLevels(levels);
  }, []);

  const activateSearchAccentBorder = useCallback(() => {
    setSearchAccentBorder(true);
  }, []);

  const handleSearchExpandedChange = useCallback((nextExpanded) => {
    setSearchStartExpanded(Boolean(nextExpanded));
    setSearchAccentBorder(Boolean(nextExpanded));
  }, []);

  const handleSearchVisualExpandedChange = useCallback((nextExpanded) => {
    setIsSearchFieldExpanded(Boolean(nextExpanded));
  }, []);

  // Click outside search box → truncate multi-line text + clear accent border
  useEffect(() => {
    const onPointerDown = (event) => {
      const bar = searchbarRef.current;
      if (!bar || bar.contains(event.target)) return;
      // Keep expanded when using discover prompt chips (they re-expand intent text)
      if (
        event.target.closest?.(
          ".scs__prompt-chips, .scs__child-prompts, .scs__prompt-chip, .scs__child-prompt-chip"
        )
      ) {
        return;
      }
      setSearchTruncateKey((n) => n + 1);
      setSearchStartExpanded(false);
      setSearchAccentBorder(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const persistFaroSession = (overrides = {}) => {
    if (skipFaroPersistRef.current) return;
    const snapshot = buildFaroSessionSnapshot({
      mode,
      queryText,
      intent,
      imageData,
      imageName,
      hasSearched,
      products,
      weak,
      activePromptKey,
      selectedPromptChildren,
      orderItems,
      isOrderOpen,
      posConfirmedCount,
      posConfirmedProductCounts,
      ...overrides,
    });
    snapshot.addedProductCounts = buildAddedProductCounts(snapshot.orderItems);
    saveFaroSession(snapshot, faroScope);
  };

  /** Successful cart/schedule → wipe this entry's Faro bucket so reopen is initial. */
  const resetFaroSessionAfterSuccessfulSchedule = () => {
    skipFaroPersistRef.current = true;
    clearFaroSession(faroScope);
    setMode("search");
    setQueryText("");
    setIntent("");
    setImageData("");
    setImageName("");
    setIsImagePreviewExpanded(true);
    setHasSearched(false);
    setProducts([]);
    setWeak(false);
    setActivePromptKey("");
    setSelectedPromptChildren({});
    selectedPromptChildrenRef.current = {};
    activePromptKeyRef.current = "";
    promptHadChildrenRef.current = {};
    lastChipIntentRef.current = "";
    prevDiscoverIntentRef.current = "";
    setOrderItems([]);
    setIsOrderOpen(false);
    setEditingOrderItemId(null);
    setOrderOpenDropdown(null);
    setActiveConfigCardKey(null);
    setRestoreRequest(null);
    setDiscoverExitStep(0);
    setSearchAccentBorder(false);
    setSearchStartExpanded(true);
    setPosConfirmedCount(0);
    setPosConfirmedProductCounts({});
  };

  useEffect(() => {
    return () => {
      if (closeNavTimeoutRef.current) {
        clearTimeout(closeNavTimeoutRef.current);
      }
      if (chipHoverLeaveTimerRef.current) {
        clearTimeout(chipHoverLeaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Minimize within the same screen remounts Faro and restores that screen's bucket only.
    // Opening Faro from a different screen uses a different scope → initial empty state.
    setFaroActiveScope(faroScope);
    const session = loadFaroSession(faroScope);
    const hasRestorableSession = Boolean(
      session &&
        (
          session.hasSearched ||
          (typeof session.queryText === "string" && session.queryText.trim()) ||
          (typeof session.intent === "string" && session.intent.trim()) ||
          (typeof session.imageData === "string" && session.imageData) ||
          (Array.isArray(session.products) && session.products.length) ||
          (Array.isArray(session.orderItems) && session.orderItems.length)
        )
    );
    const shouldRestore =
      Boolean(routeState?.fromCustomOrder || routeState?.fromFaro) ||
      hasRestorableSession;

    if (shouldRestore && session) {
      setMode(session.mode === "discover" ? "discover" : "search");
      setQueryText(session.queryText || "");
      setIntent(session.intent || "");
      setImageData(session.imageData || "");
      setImageName(session.imageName || "");
      setProducts(Array.isArray(session.products) ? session.products : []);
      setWeak(Boolean(session.weak));
      setHasSearched(Boolean(session.hasSearched));
      setActivePromptKey(session.activePromptKey || "");
      setSelectedPromptChildren(session.selectedPromptChildren || {});
      lastChipIntentRef.current = session.intent || "";
      prevDiscoverIntentRef.current = session.intent || "";
      setOrderItems(Array.isArray(session.orderItems) ? session.orderItems : []);
      setIsOrderOpen(Boolean(session.isOrderOpen));
      setPosConfirmedCount(Number(session.posConfirmedCount) || 0);
      setPosConfirmedProductCounts(
        session.posConfirmedProductCounts &&
          typeof session.posConfirmedProductCounts === "object"
          ? session.posConfirmedProductCounts
          : {}
      );

      const restoreText =
        session.mode === "discover" ? session.intent || "" : session.queryText || "";
      const hasProducts = Array.isArray(session.products) && session.products.length > 0;
      if (session.hasSearched && !hasProducts && restoreText.trim()) {
        // Re-run request so reopen shows loading/results state instead of stale "No matches found".
        setRestoreRequest({
          mode: session.mode === "discover" ? "discover" : "search",
          text: restoreText.trim(),
        });
      } else {
        setRestoreRequest(null);
      }
    }

    setSessionReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionReady) return;
    persistFaroSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sessionReady,
    mode,
    queryText,
    intent,
    imageData,
    imageName,
    hasSearched,
    products,
    weak,
    activePromptKey,
    selectedPromptChildren,
    orderItems,
    isOrderOpen,
    posConfirmedCount,
    posConfirmedProductCounts,
  ]);

  useEffect(() => {
    const field = searchTextRef.current;
    if (!field || field.tagName !== "TEXTAREA") return;
    field.style.height = "21px";
    const value = mode === "discover" ? intent : queryText;
    field.style.height = value.trim()
      ? `${Math.min(field.scrollHeight, 118)}px`
      : "21px";
  }, [queryText, intent, mode, searchViewResetKey, searchStartExpanded]);

  const openImageUploadModal = () => {
    if (isLoading) return;
    setIsImageUploadOpen(true);
  };

  const closeImageUploadModal = () => {
    setIsImageUploadOpen(false);
  };

  const openProductGallery = (product) => {
    if (!product) return;
    setGalleryProduct(product);
  };

  const closeProductGallery = () => {
    setGalleryProduct(null);
  };

  const openProductConfig = (cardKey) => {
    if (!cardKey) return;
    setActiveConfigCardKey(cardKey);
  };

  const closeProductConfig = () => {
    setActiveConfigCardKey(null);
  };

  const clearImage = () => {
    setImageData("");
    setImageName("");
    setIsImagePreviewExpanded(true);
  };

  const resetResults = () => {
    setProducts([]);
    setWeak(false);
    setOrderItems([]);
    setIsOrderOpen(false);
    setEditingOrderItemId(null);
    setOrderOpenDropdown(null);
    setActiveConfigCardKey(null);
  };

  const handleConfirmSelection = ({ product, selections }) => {
    if (isFromQuickview) {
      const motherSku = getMotherSku(product);
      if (!motherSku) {
        error(t("Unable to load the selected product in Quick View."));
        return;
      }

      // Keep Quickview Faro search/results until checkout (same as POS screen)
      persistFaroSession();
      navigate("/app/pos/Quickview", {
        state: {
          ...(routeState?.quickviewState || {}),
          faroMotherSku: motherSku,
          faroSelections: selections,
          faroReturnKey: Date.now(),
        },
      });
      return;
    }

    const item = buildOrderItem({
      product,
      selections,
      inStock: Boolean(filters.inStock),
    });
    setOrderItems((items) => [...items, item]);
    showItemAddedToast(t("Item added to order."));
  };

  const handleDeleteOrderItem = (itemId) => {
    setOrderItems((items) => {
      const nextItems = items.filter((item) => item.id !== itemId);
      if (!nextItems.length) {
        setIsOrderOpen(false);
      }
      return nextItems;
    });
    if (editingOrderItemId === itemId) {
      setEditingOrderItemId(null);
      setOrderOpenDropdown(null);
    }
  };

  const handleEditOrderItem = (item) => {
    setEditingOrderItemId((current) => (current === item.id ? null : item.id));
    setOrderOpenDropdown(null);
  };

  const handleUpdateOrderItem = (itemId, selections) => {
    setOrderItems((items) =>
      items.map((entry) => {
        if (entry.id !== itemId) return entry;
        const next = buildOrderItem({
          product: entry.product,
          selections,
          inStock: entry.inStock,
        });
        return { ...next, id: itemId };
      })
    );
  };

  const handleOrderDropdownToggle = (dropdownId) => {
    setOrderOpenDropdown((current) => (current === dropdownId ? null : dropdownId));
  };

  const handleConfirmOrderEdit = () => {
    setEditingOrderItemId(null);
    setOrderOpenDropdown(null);
  };

  const handlePosscreenOrderSubmit = async () => {
    const items = orderItems
      .map((item) => item.variantCombinationId)
      .filter(Boolean);

    if (!items.length) {
      error(t("Please select at least one item to order."));
      return;
    }

    if (items.length !== orderItems.length) {
      error(t("Unable to add one or more items to cart. Please check variant selection."));
      return;
    }

    setOrderSubmitting(true);
    const GetloadingID = loading();
    try {
      const postdata = {
        order_type: "pos_order",
        customer_id: customer?.id,
        sales_person_id: saleperson?.value,
        items: items.map((product_id) => ({ product_id })),
      };

      const res = await _Api(postdata, ADD_INVENTORY_ITEMS_TO_CART_ENDPOINT);
      if (res?.code !== 200) {
        Updateloading(t, GetloadingID, res?.message || t("Something went wrong"), "error");
        return;
      }

      const added = Array.isArray(res?.data?.added) ? res.data.added : [];
      const failed = Array.isArray(res?.data?.failed) ? res.data.failed : [];
      const addedCount = Number(
        res?.data?.added_count ?? added.filter((entry) => entry?.ok).length
      );
      const failedCount = Number(
        res?.data?.failed_count ?? failed.filter((entry) => !entry?.ok).length
      );
      const responseMessage = res?.message || t("Successfully Add to Cart!!");

      if (failedCount > 0 && addedCount === 0) {
        const failureReason =
          failed.find((entry) => entry?.reason)?.reason || responseMessage;
        Updateloading(t, GetloadingID, failureReason, "error");
      } else {
        Updateloading(t, GetloadingID, responseMessage);
      }

      setOrderItems([]);
      setIsOrderOpen(false);
      setEditingOrderItemId(null);
      setOrderOpenDropdown(null);
      // POS screen / Quickview: keep Faro search+results until checkout completes.
      // Other scopes still reset this Faro bucket after schedule.
      if (
        faroScope === FARO_SCOPES.POSSCREEN ||
        faroScope === FARO_SCOPES.QUICKVIEW
      ) {
        const submittedCount = items.length;
        const nextConfirmedCount = posConfirmedCount + submittedCount;
        const nextProductCounts = { ...posConfirmedProductCounts };
        orderItems.forEach((entry) => {
          const identity = getProductIdentity(entry?.product);
          if (!identity) return;
          nextProductCounts[identity] = (nextProductCounts[identity] || 0) + 1;
        });
        setPosConfirmedCount(nextConfirmedCount);
        setPosConfirmedProductCounts(nextProductCounts);
        persistFaroSession({
          orderItems: [],
          isOrderOpen: false,
          posConfirmedCount: nextConfirmedCount,
          posConfirmedProductCounts: nextProductCounts,
        });
      } else {
        resetFaroSessionAfterSuccessfulSchedule();
      }
      navigate("/app/pos/posscreen");
    } catch (err) {
      console.log(err, "err");
      Updateloading(t, GetloadingID, t("Something went wrong"), "error");
    } finally {
      setOrderSubmitting(false);
    }
  };
  // Menu Faro: user can toggle both modes; submit splits by item.inStock
  const isFromMenu = faroScope === FARO_SCOPES.MENU;

  // Create Your Own inventory only (not menu / products / posscreen POS cart)
  const isInventoryCustomOrder = Boolean(
    !isFromPosscreen &&
      !isFromProducts &&
      !isFromMenu &&
      (filters.inStock ||
        routeState?.inStock ||
        routeState?.filtertype === false)
  );

  const extractCustomCartId = (responseData, fallback) =>
    responseData?.cartid ||
    responseData?.cart_id ||
    responseData?.custom_cart_id ||
    (typeof responseData === "string" ? responseData : null) ||
    responseData?.items?.[0]?.custom_cart_id ||
    responseData?.items?.[0]?.cartid ||
    (Array.isArray(responseData) ? responseData[0]?.custom_cart_id : null) ||
    fallback ||
    undefined;

  const submitInventoryItemsToCustomCart = async (productIds, cartid, GetloadingID) => {
    const postdata = {
      items: productIds.map((product_id) => ({ product_id })),
    };
    if (cartid) {
      postdata.cartid = cartid;
    }
    if (customer?.id) {
      postdata.customer_id = customer.id;
    }
    if (saleperson?.value) {
      postdata.sales_person_id = saleperson.value;
    }

    const res = await _Api(
      postdata,
      ADD_INVENTORY_ITEMS_TO_CUSTOM_ORDER_ENDPOINT
    );

    if (res?.code !== 200 && res?.success !== true) {
      const message = res?.message || t("Something went wrong");
      if (GetloadingID) {
        Updateloading(t, GetloadingID, message, "error");
      } else {
        error(message);
      }
      return null;
    }

    return extractCustomCartId(res?.data, cartid);
  };

  const submitCatalogItemsToCustomCart = async (productsIds, cartid, isAppendFlow, GetloadingID) => {
    let nextCartId = cartid;

    if (isAppendFlow) {
      for (const productId of productsIds) {
        const res = await _Api(
          {
            product_id: productId,
            type: "catalog",
            customer_id: customer?.id,
            sales_person_id: saleperson?.value,
            order_type: "custom_order",
          },
          "api/v1/POS/customOrder/addItemToCart"
        );

        if (res?.code !== 200) {
          const message = t("This Product Already in cart!!");
          if (GetloadingID) {
            Updateloading(t, GetloadingID, message, "error");
          } else {
            error(message);
          }
          return null;
        }

        nextCartId = res?.data || nextCartId;
      }
      return nextCartId;
    }

    const clearRes = await _Api({}, "api/v1/POS/customOrder/cartClear");
    if (clearRes?.code !== 200) {
      const message = t("Something went wrong");
      if (GetloadingID) {
        Updateloading(t, GetloadingID, message, "error");
      } else {
        error(message);
      }
      return null;
    }

    localStorage.removeItem("showcheckout");
    const postdata = {
      product_type: "catalog",
      order_type: "custom_order",
      customer_id: customer?.id,
      sales_person_id: saleperson?.value,
      productsIds,
    };

    const res = await _Api(postdata, "api/v1/POS/home/addToCarts");
    if (res?.success !== true) {
      const message = t("This Product Already in cart!!");
      if (GetloadingID) {
        Updateloading(t, GetloadingID, message, "error");
      } else {
        error(message);
      }
      return null;
    }

    return res?.data || nextCartId;
  };

  // Menu Faro only: split by how each item was added (inStock toggle)
  const handleMenuCustomOrderSubmit = async () => {
    const inventoryItems = orderItems.filter((item) => item.inStock);
    const catalogItems = orderItems.filter((item) => !item.inStock);

    const inventoryProductIds = inventoryItems
      .map((item) => item.variantCombinationId)
      .filter(Boolean);
    const catalogProductIds = catalogItems
      .map((item) => item.variantCombinationId)
      .filter(Boolean);

    if (
      inventoryProductIds.length !== inventoryItems.length ||
      catalogProductIds.length !== catalogItems.length
    ) {
      error(
        t("Unable to add one or more items to cart. Please check variant selection.")
      );
      return;
    }

    setOrderSubmitting(true);
    const GetloadingID = loading();
    try {
      let nextCartId;

      // Catalog (inStock false) → addToCarts
      if (catalogProductIds.length) {
        nextCartId = await submitCatalogItemsToCustomCart(
          catalogProductIds,
          nextCartId,
          false,
          GetloadingID
        );
        if (nextCartId === null) return;
      }

      // Inventory (inStock true) → addInventoryItemsToCustomOrder
      if (inventoryProductIds.length) {
        const inventoryCartId = await submitInventoryItemsToCustomCart(
          inventoryProductIds,
          nextCartId,
          GetloadingID
        );
        if (inventoryCartId === null) return;
        nextCartId = inventoryCartId || nextCartId;
      }

      Updateloading(t, GetloadingID, t("Successfully Add to Cart!!"));

      setOrderItems([]);
      setIsOrderOpen(false);
      setEditingOrderItemId(null);
      setOrderOpenDropdown(null);
      resetFaroSessionAfterSuccessfulSchedule();
      setFaroCustomOrderActive(true);

      navigate("/app/pos/custom/createYourOwnSetting", {
        state: {
          ...(nextCartId ? { cartid: nextCartId } : {}),
          filtertype: catalogItems.length > 0 ? true : false,
          fromFaro: true,
        },
      });
    } catch (err) {
      console.log(err, "err");
      Updateloading(t, GetloadingID, t("Something went wrong"), "error");
    } finally {
      setOrderSubmitting(false);
    }
  };

  const handleInventoryCustomOrderSubmit = async () => {
    const items = orderItems
      .map((item) => item.variantCombinationId)
      .filter(Boolean)
      .map((product_id) => ({ product_id }));

    if (!items.length) {
      error(t("Please select at least one item to order."));
      return;
    }

    if (items.length !== orderItems.length) {
      error(t("Unable to add one or more items to cart. Please check variant selection."));
      return;
    }

    setOrderSubmitting(true);
    const GetloadingID = loading();
    try {
      const postdata = { items };
      if (routeState?.cartid) {
        postdata.cartid = routeState.cartid;
      }
      if (customer?.id) {
        postdata.customer_id = customer.id;
      }
      if (saleperson?.value) {
        postdata.sales_person_id = saleperson.value;
      }

      const res = await _Api(
        postdata,
        ADD_INVENTORY_ITEMS_TO_CUSTOM_ORDER_ENDPOINT
      );

      if (res?.code !== 200 && res?.success !== true) {
        Updateloading(t, GetloadingID, res?.message || t("Something went wrong"), "error");
        return;
      }

      Updateloading(t, GetloadingID, res?.message || t("Successfully Add to Cart!!"));

      const nextCartId = extractCustomCartId(res?.data, routeState?.cartid);

      setOrderItems([]);
      setIsOrderOpen(false);
      setEditingOrderItemId(null);
      setOrderOpenDropdown(null);
      resetFaroSessionAfterSuccessfulSchedule();
      setFaroCustomOrderActive(true);

      navigate("/app/pos/custom/createYourOwnSetting", {
        state: {
          ...(nextCartId ? { cartid: nextCartId } : {}),
          filtertype: false,
          fromFaro: true,
          order_id: routeState?.order_id,
          order_edit: routeState?.order_edit,
          order_type: routeState?.order_type,
        },
      });
    } catch (err) {
      console.log(err, "err");
      Updateloading(t, GetloadingID, t("Something went wrong"), "error");
    } finally {
      setOrderSubmitting(false);
    }
  };

  const handleOrderSubmit = async () => {
    if (!orderItems.length) {
      error(t("Please select at least one item to order."));
      return;
    }

    if (isInventoryPosCart) {
      await handlePosscreenOrderSubmit();
      return;
    }

    // Menu Faro only: split by inStock at add-time
    if (isFromMenu) {
      await handleMenuCustomOrderSubmit();
      return;
    }

    if (isInventoryCustomOrder) {
      await handleInventoryCustomOrderSubmit();
      return;
    }

    const productsIds = orderItems
      .map((item) => item.variantCombinationId)
      .filter(Boolean);
    if (productsIds.length !== orderItems.length) {
      error(t("Unable to add one or more items to cart. Please check variant selection."));
      return;
    }

    const isAppendFlow = Boolean(routeState?.fromCustomOrder && routeState?.cartid);

    setOrderSubmitting(true);
    const GetloadingID = loading();
    try {
      let nextCartId = routeState?.cartid;

      if (isAppendFlow) {
        for (const productId of productsIds) {
          const res = await _Api(
            {
              product_id: productId,
              type: "catalog",
              customer_id: customer?.id,
              sales_person_id: saleperson?.value,
              order_type: "custom_order",
            },
            "api/v1/POS/customOrder/addItemToCart"
          );

          if (res?.code !== 200) {
            Updateloading(t, GetloadingID, t("This Product Already in cart!!"), "error");
            return;
          }

          nextCartId = res?.data || nextCartId;
        }

        Updateloading(t, GetloadingID, t("Successfully Add to Cart!!"));
      } else {
        const clearRes = await _Api({}, "api/v1/POS/customOrder/cartClear");
        if (clearRes?.code !== 200) {
          Updateloading(t, GetloadingID, t("Something went wrong"), "error");
          return;
        }

        localStorage.removeItem("showcheckout");
        const postdata = {
          product_type: "catalog",
          order_type: "custom_order",
          customer_id: customer?.id,
          sales_person_id: saleperson?.value,
          productsIds,
        };

        const res = await _Api(postdata, "api/v1/POS/home/addToCarts");
        if (res?.success !== true) {
          Updateloading(t, GetloadingID, t("This Product Already in cart!!"), "error");
          return;
        }

        Updateloading(t, GetloadingID, t(res?.message) || t("Successfully Add to Cart!!"));
        nextCartId = res?.data;
      }

      setOrderItems([]);
      setIsOrderOpen(false);
      setEditingOrderItemId(null);
      setOrderOpenDropdown(null);
      resetFaroSessionAfterSuccessfulSchedule();
      setFaroCustomOrderActive(true);

      navigate("/app/pos/custom/createYourOwnSetting", {
        state: {
          cartid: nextCartId,
          filtertype: routeState?.filtertype ?? true,
          fromFaro: true,
          order_id: routeState?.order_id,
          order_edit: routeState?.order_edit,
          order_type: routeState?.order_type,
        },
      });
    } catch (err) {
      console.log(err, "err");
      Updateloading(t, GetloadingID, t("Something went wrong"), "error");
    } finally {
      setOrderSubmitting(false);
    }
  };

  const handleResponse = (res) => {
    if (!res) return;
    if (!res.success) {
      error(res?.message || t("Something went wrong"));
      resetResults();
      return;
    }
    setProducts(Array.isArray(res.products) ? res.products : []);
    setWeak(Boolean(res.weak));
    setActiveConfigCardKey(null);
  };

  const runSearch = async (override, filtersOverride, imageOverride) => {
    const text = typeof override === "string" ? override : queryText;
    const image =
      typeof imageOverride === "string" ? imageOverride : imageData;
    if (!text.trim() && !image) {
      error(t("Enter a search text or pick an image"));
      return;
    }
    const postdata = { distill, limit: Number(limit) || 20 };
    if (text.trim()) postdata.queryText = text.trim();
    if (image) postdata.queryImageBase64 = image;
    const appliedFilters = buildFilters(filtersOverride || filters);
    if (Object.keys(appliedFilters).length) postdata.filters = appliedFilters;

    setIsLoading(true);
    setHasSearched(true);
    // Results page: show truncated text first
    setSearchStartExpanded(false);
    setSearchViewResetKey((n) => n + 1);
    try {
      const res = await _Api(postdata, SEARCH_ENDPOINT);
      handleResponse(res);
    } catch (err) {
      console.log(err, "err");
    } finally {
      setIsLoading(false);
    }
  };

  const collapseImagePreview = () => {
    setIsImagePreviewExpanded(false);
  };

  const expandImagePreview = () => {
    setIsImagePreviewExpanded(true);
  };

  // Scenario 2: click image thumb/preview → toggle expand/collapse (any scroll position)
  const toggleImagePreview = () => {
    if (isImagePreviewExpanded) collapseImagePreview();
    else expandImagePreview();
  };

  // Scenario 1: scroll product grid → auto-collapse only when last row is reached
  const handleResultsListScroll = (event) => {
    if (!imageData || !isImagePreviewExpanded) return;

    const list = event.currentTarget;
    const scrollTop = list.scrollTop;
    const scrollingDown = scrollTop > resultsListScrollTopRef.current + 1;
    resultsListScrollTopRef.current = scrollTop;
    if (!scrollingDown) return;

    // Nothing to scroll → last row is already on screen; click handles collapse
    if (list.scrollHeight <= list.clientHeight + 4) return;

    const grid = list.querySelector(".scs__grid");
    const cards = grid ? Array.from(grid.children) : [];
    if (!cards.length) {
      const distanceFromBottom =
        list.scrollHeight - list.scrollTop - list.clientHeight;
      if (distanceFromBottom <= 48) collapseImagePreview();
      return;
    }

    const lastCard = cards[cards.length - 1];
    const lastRowOffsetTop = lastCard.offsetTop;
    const firstOfLastRow =
      cards.find((card) => Math.abs(card.offsetTop - lastRowOffsetTop) < 2) ||
      lastCard;

    const listRect = list.getBoundingClientRect();
    const rowRect = firstOfLastRow.getBoundingClientRect();
    // Last grid row has entered the visible product list area
    if (rowRect.top <= listRect.bottom - 12) {
      collapseImagePreview();
    }
  };

  const applyImageFile = async (file) => {
    if (!file) return;
    if (!String(file.type || "").startsWith("image/")) {
      error(t("Please choose an image file"));
      return;
    }
    let dataUrl;
    try {
      dataUrl = await fileToDataUrl(file);
    } catch (err) {
      error(t("Could not read the selected image"));
      return;
    }
    setMode("search");
    setImageData(dataUrl);
    setImageName(file.name);
    setIsImagePreviewExpanded(true);
    setIsImageUploadOpen(false);
    setSearchAccentBorder(true);
    // Auto-run image search — no Discovery / arrow click required
    await runSearch(queryText, undefined, dataUrl);
  };

  const runDiscover = async (override, filtersOverride) => {
    const text = typeof override === "string" ? override : intent;
    if (!text.trim()) {
      error(t("Describe what you're looking for"));
      return;
    }
    const postdata = { intent: text.trim(), limit: Number(limit) || 10 };
    const appliedFilters = buildFilters(filtersOverride || filters);
    if (Object.keys(appliedFilters).length) postdata.filters = appliedFilters;

    setIsLoading(true);
    setHasSearched(true);
    // Results page: show truncated text first
    setSearchStartExpanded(false);
    setSearchAccentBorder(false);
    setDiscoverExitStep(0);
    setSearchViewResetKey((n) => n + 1);
    try {
      const res = await _Api(postdata, DISCOVER_ENDPOINT);
      handleResponse(res);
    } catch (err) {
      console.log(err, "err");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInStockOnlyChange = (event) => {
    const checked = Boolean(event.target.checked);
    const nextFilters = { ...filters, inStock: checked };
    setFilters(nextFilters);
    if (!hasSearched) return;
    if (mode === "discover") {
      runDiscover(undefined, nextFilters);
    } else {
      runSearch(undefined, nextFilters);
    }
  };

  useEffect(() => {
    if (!sessionReady || !restoreRequest) return;
    const { mode: restoreMode, text } = restoreRequest;
    if (!text?.trim()) {
      setRestoreRequest(null);
      return;
    }
    if (restoreMode === "discover") {
      runDiscover(text);
    } else {
      runSearch(text);
    }
    setRestoreRequest(null);
    // Intentionally using current function closures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionReady, restoreRequest]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === "search") runSearch();
    else runDiscover();
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleExample = (text) => {
    if (mode === "search") {
      setQueryText(text);
      runSearch(text);
    } else {
      setIntent(text);
      runDiscover(text);
    }
  };

  const appendPromptSegment = (current, segment) => {
    const trimmed = current.trim();
    if (!trimmed) return segment;
    return `${/[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`} ${segment}`;
  };

  const formatPromptList = (values) => {
    if (values.length <= 1) return values[0] || "";
    if (values.length === 2) return `${values[0]} and ${values[1]}`;
    return `${values.slice(0, -1).join(", ")}, and ${values[values.length - 1]}`;
  };

  const getChildPhrase = (group, child) => {
    if (group.key === "recipient") {
      return child === "Myself" ? "myself" : `my ${child.toLowerCase()}`;
    }
    if (group.key === "budget" || group.key === "size") {
      return child;
    }
    return child.toLowerCase();
  };

  const buildPromptSentence = (group, children) => {
    const list = formatPromptList(children.map((child) => getChildPhrase(group, child)));
    if (!list) return group.starter;

    switch (group.key) {
      case "recipient":
        return `I'm shopping for ${list}.`;
      case "occasion":
        return `This jewelry is for ${list}.`;
      case "jewelryType":
        return `I'm looking for ${list}.`;
      case "metal":
        return `I'd like it to be made of ${list}.`;
      case "gemstone":
        return `I'd like it to include ${list}.`;
      case "style":
        return `I prefer a ${list} style.`;
      case "budget":
        return `My budget is ${list}.`;
      case "theme":
        return `I'm looking for a design inspired by ${list}.`;
      case "color":
        return `I'm looking for ${list} tones.`;
      case "size":
        return `The size is ${list}.`;
      case "lifestyle":
        return `It will mostly be worn for ${list}.`;
      case "avoid":
        return `I'd like to avoid ${list}.`;
      default:
        return group.makeSentence(children[children.length - 1]);
    }
  };

  // Prefix before "..." in starter, e.g. "I'm shopping for" / "I'm looking for a"
  const getStarterPrefix = (starter) => {
    const raw = String(starter || "");
    const idx = raw.indexOf("...");
    return (idx === -1 ? raw : raw.slice(0, idx)).replace(/\s+/g, " ").trim();
  };

  // Prefix used in built child sentences (may differ from starter, e.g. jewelryType drops "a")
  const getSentencePrefix = (group) => {
    const sampleChild = group.children[0];
    if (!sampleChild) return getStarterPrefix(group.starter);
    const phrase = getChildPhrase(group, sampleChild);
    const built = buildPromptSentence(group, [sampleChild]);
    const at = built.toLowerCase().indexOf(String(phrase).toLowerCase());
    if (at > 0) return built.slice(0, at).replace(/\s+/g, " ").trim();
    return getStarterPrefix(group.starter);
  };

  // Split parent starter at "..." → prefix + suffix.
  // Keeps "..." in the real field value; on child hover ghost fills that slot.
  // e.g. "I prefer a... style." → prefix "I prefer a ", suffix " style."
  const splitStarterAtEllipsis = (starter) => {
    const raw = String(starter || "");
    const idx = raw.indexOf("...");
    if (idx === -1) {
      return {
        prefix: raw.endsWith(" ") ? raw : `${raw} `,
        suffix: "",
      };
    }
    let prefix = raw.slice(0, idx);
    const suffix = raw.slice(idx + 3);
    if (prefix && !prefix.endsWith(" ")) prefix = `${prefix} `;
    return { prefix, suffix };
  };

  // Parent prompt parts from starter (preserves "..." in selected parent text).
  const getParentPromptParts = (group) => splitStarterAtEllipsis(group.starter);

  const getParentEditableText = (group) => group.starter;

  const isExactParentPromptText = (intent, group) => {
    const text = String(intent || "");
    if (text.includes(group.starter)) return true;
    const { prefix } = getParentPromptParts(group);
    return (
      text === prefix ||
      text === prefix.trimEnd() ||
      text.endsWith(prefix) ||
      text.endsWith(prefix.trimEnd()) ||
      text.endsWith(group.starter)
    );
  };

  const hasExactParentStarter = (intent, starter) =>
    String(intent || "").includes(starter);

  // Point 3: parent remnant still there after child text removed
  const hasParentRemnantLeft = (intent, group) => {
    const text = String(intent || "");
    if (hasExactParentStarter(text, group.starter)) return true;
    const starterPrefix = getStarterPrefix(group.starter);
    if (starterPrefix && text.includes(starterPrefix)) return true;
    const sentencePrefix = getSentencePrefix(group);
    if (sentencePrefix && text.includes(sentencePrefix)) return true;
    return false;
  };

  // Bounded child-phrase hit (avoids "ring" inside "earrings", Fiancé inside Fiancée).
  const findBoundedChildPhrase = (text, group, child, fromIndex = 0) => {
    const phrase = String(getChildPhrase(group, child) || "")
      .trim()
      .toLowerCase();
    if (!phrase) return null;
    const lower = String(text || "").toLowerCase();
    let from = Math.max(0, fromIndex);
    while (from <= lower.length) {
      const idx = lower.indexOf(phrase, from);
      if (idx === -1) return null;
      const end = idx + phrase.length;
      const before = idx === 0 ? " " : lower[idx - 1];
      const afterCh = end >= lower.length ? "" : lower[end];
      const afterSlice = lower.slice(end);
      const beforeOk =
        idx === 0 ||
        /[\s,]/.test(before) ||
        lower.slice(Math.max(0, idx - 4), idx) === "and ";
      const afterOk =
        end === lower.length ||
        /[\s,.!?]/.test(afterCh) ||
        afterSlice.startsWith(" and") ||
        afterSlice.startsWith(",") ||
        /^and\b/.test(afterSlice);
      const fiancéOk = child !== "Fiancé" || afterCh !== "e";
      if (beforeOk && afterOk && fiancéOk) {
        return { start: idx, end };
      }
      from = idx + 1;
    }
    return null;
  };

  // Single OR multi: only the child whose phrase was edited/deleted deselects.
  // Untouched siblings stay selected.
  const resolveKeptChildrenFromIntent = (group, children, intent, prevIntent = "") => {
    if (!children.length) return [];
    const text = String(intent || "");
    const prevText = String(prevIntent || "");
    const expectedSentence = buildPromptSentence(group, children);

    if (text.includes(expectedSentence)) {
      return children;
    }

    const phraseOf = (child) =>
      String(getChildPhrase(group, child) || "")
        .trim()
        .toLowerCase();

    const hasListShape = /\s+and\s+|,\s+/i.test(text);

    // One clean child sentence (no list leftovers): any edit of that sentence
    // deselects that child only. Skip when leftover "a, and b" text remains
    // after a sibling was already dropped — then use phrase matching below.
    if (children.length === 1 && !hasListShape) {
      const only = children[0];
      const makeForm =
        typeof group.makeSentence === "function" ? group.makeSentence(only) : "";
      if (makeForm && text.includes(makeForm)) return children;
      return [];
    }

    // Multi (or single + leftover list): keep only intact phrases.
    // Longest first so "my fiancée" / "white gold" win overlapping shorter hits.
    const byLongest = [...children].sort(
      (a, b) => phraseOf(b).length - phraseOf(a).length
    );

    const collectIntact = (source) => {
      const intact = new Set();
      const usedRanges = [];
      const overlaps = (start, end) =>
        usedRanges.some(([s, e]) => start < e && end > s);

      byLongest.forEach((child) => {
        let from = 0;
        while (from <= source.length) {
          const hit = findBoundedChildPhrase(source, group, child, from);
          if (!hit) break;
          if (!overlaps(hit.start, hit.end)) {
            intact.add(child);
            usedRanges.push([hit.start, hit.end]);
            break;
          }
          from = hit.start + 1;
        }
      });
      return intact;
    };

    const nextIntact = collectIntact(text);

    // Prefer prev→next: drop only phrases that disappeared after this edit.
    // Siblings that are still present stay selected.
    if (prevText && prevText !== text) {
      const prevIntact = collectIntact(prevText);
      return children.filter((child) => {
        const wasPresent = prevIntact.has(child);
        const stillPresent = nextIntact.has(child);
        if (wasPresent && !stillPresent) return false;
        if (stillPresent) return true;
        return false;
      });
    }

    return children.filter((child) => nextIntact.has(child));
  };

  const getGroupSegmentCandidates = (group, children = []) => {
    const candidates = [];
    if (children.length) {
      candidates.push(buildPromptSentence(group, children));
      // Also prior single/multi subsets so we can REPLACE instead of append
      if (children.length > 1) {
        children.forEach((child) => {
          candidates.push(buildPromptSentence(group, [child]));
        });
      }
    }
    candidates.push(group.starter);
    candidates.push(getParentEditableText(group));
    candidates.push(getParentEditableText(group).trimEnd());
    // Built sentences (no "a") + makeSentence variants (with "a") for every child
    group.children.forEach((child) => {
      candidates.push(buildPromptSentence(group, [child]));
      candidates.push(group.makeSentence(child));
    });
    return [...new Set(candidates.filter(Boolean))].sort((a, b) => b.length - a.length);
  };

  const findGroupSegmentInIntent = (intent, group, children = []) => {
    const text = String(intent || "");
    const candidates = getGroupSegmentCandidates(group, children);
    for (const candidate of candidates) {
      const index = text.lastIndexOf(candidate);
      if (index !== -1) {
        return { index, length: candidate.length, match: candidate };
      }
    }

    // Locate this group's sentence by its built prefix (e.g. jewelryType:
    // "I'm looking for" — starter has "a..." but child sentences drop "a").
    const sentencePrefix = getSentencePrefix(group);
    if (sentencePrefix) {
      const index = text.toLowerCase().lastIndexOf(sentencePrefix.toLowerCase());
      if (index !== -1) {
        const afterPrefix = text.slice(index + sentencePrefix.length);
        const periodRel = afterPrefix.search(/[.!?]/);
        const length =
          periodRel === -1
            ? text.length - index
            : sentencePrefix.length + periodRel + 1;
        return { index, length, match: text.slice(index, index + length) };
      }
    }

    // Fallback: starter core through end of that sentence
    const core = String(group.starter || "")
      .replace(/\.\.\.\s*$/, "")
      .replace(/\.\.\./g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!core) return null;
    const index = text.lastIndexOf(core);
    if (index === -1) return null;
    const afterCore = text.slice(index + core.length);
    const periodRel = afterCore.search(/[.!?]/);
    const length =
      periodRel === -1
        ? text.length - index
        : core.length + periodRel + 1;
    return { index, length, match: text.slice(index, index + length) };
  };

  // True when intent already has a filled-in sentence for this group (not the "..." starter).
  // Used so re-selecting a parent appends a new sentence instead of rewriting the old one.
  const hasCompletedGroupSentenceInIntent = (text, group, children = []) => {
    const found = findGroupSegmentInIntent(String(text || ""), group, children);
    if (!found) return false;
    const match = String(found.match || "").trim();
    if (!match) return false;
    if (match === String(group.starter || "").trim()) return false;
    if (match.includes("...")) return false;
    return true;
  };

  // Point 5: always rebuild a clean full sentence for the group (replace, never duplicate)
  // When the same parent is re-opened after a completed sentence, prefer the trailing
  // incomplete starter (last "...") so child chips update the new sentence only.
  const upsertGroupSentence = (
    current,
    group,
    children,
    previousChildren = []
  ) => {
    const trimmed = String(current || "").trim();
    // Parent-only keeps starter with "..." (e.g. "I prefer a... style.")
    const nextSentence = children.length
      ? buildPromptSentence(group, children)
      : group.starter;

    // Prefer replacing a trailing incomplete starter when one exists after older sentences
    const starter = String(group.starter || "");
    let found = null;
    if (starter) {
      const starterIndex = trimmed.lastIndexOf(starter);
      if (starterIndex !== -1) {
        const afterStarter = trimmed.slice(starterIndex + starter.length).trim();
        // Trailing starter (nothing after) → update this new writing slot
        if (!afterStarter) {
          found = { index: starterIndex, length: starter.length, match: starter };
        }
      }
    }

    // Find existing group text using previous children first (what is currently in the field)
    if (!found) {
      found =
        findGroupSegmentInIntent(trimmed, group, previousChildren) ||
        findGroupSegmentInIntent(trimmed, group, children) ||
        findGroupSegmentInIntent(trimmed, group, []);
    }

    if (found) {
      const before = trimmed.slice(0, found.index).trim();
      const after = trimmed.slice(found.index + found.length).trim();
      return [before, nextSentence, after].filter(Boolean).join(" ").trim();
    }

    return appendPromptSegment(trimmed, nextSentence);
  };

  const removeGroupSentenceCompletely = (current, group, children = []) => {
    const trimmed = String(current || "").trim();
    // Prefer removing a trailing incomplete starter so older completed sentences stay.
    const starter = String(group.starter || "");
    let found = null;
    if (starter) {
      const starterIndex = trimmed.lastIndexOf(starter);
      if (starterIndex !== -1) {
        const afterStarter = trimmed.slice(starterIndex + starter.length).trim();
        if (!afterStarter) {
          found = { index: starterIndex, length: starter.length, match: starter };
        }
      }
    }
    if (!found) {
      found = findGroupSegmentInIntent(trimmed, group, children);
    }
    if (!found) return trimmed;
    const before = trimmed.slice(0, found.index).trim();
    const after = trimmed.slice(found.index + found.length).trim();
    return [before, after].filter(Boolean).join(" ").trim();
  };

  // Points 1-4 only (points 5-6 chip rebuild left unchanged)
  const handleDiscoverIntentChange = (nextIntent) => {
    const prevIntent = prevDiscoverIntentRef.current;
    setIntent(nextIntent);

    if (!String(nextIntent || "").trim()) {
      if (activePromptKeyRef.current) setActivePromptKey("");
      setSelectedPromptChildren((prev) => (Object.keys(prev).length ? {} : prev));
      selectedPromptChildrenRef.current = {};
      activePromptKeyRef.current = "";
      promptHadChildrenRef.current = {};
      lastChipIntentRef.current = "";
      prevDiscoverIntentRef.current = nextIntent;
      return;
    }

    const prevChildrenMap = selectedPromptChildrenRef.current || {};
    const childrenUpdates = {};

    DISCOVER_PROMPT_GROUPS.forEach((group) => {
      const children = prevChildrenMap[group.key] || [];
      if (!children.length) return;

      const keptInOrder = resolveKeptChildrenFromIntent(
        group,
        children,
        nextIntent,
        prevIntent
      );
      // Only drop children whose own phrase was edited/deleted — keep the rest
      if (keptInOrder.length !== children.length) {
        childrenUpdates[group.key] = keptInOrder;
      }
    });

    if (Object.keys(childrenUpdates).length) {
      const nextMap = { ...prevChildrenMap, ...childrenUpdates };
      selectedPromptChildrenRef.current = nextMap;
      setSelectedPromptChildren(nextMap);
    }

    prevDiscoverIntentRef.current = nextIntent;

    const activeKey = activePromptKeyRef.current;
    const activeGroup = DISCOVER_PROMPT_GROUPS.find((group) => group.key === activeKey);
    if (!activeGroup) return;

    const previousChildren = prevChildrenMap[activeGroup.key] || [];
    const nextChildren =
      childrenUpdates[activeGroup.key] !== undefined
        ? childrenUpdates[activeGroup.key]
        : previousChildren;

    if (previousChildren.length > 0) {
      promptHadChildrenRef.current[activeGroup.key] = true;
    }

    // Still have child chip(s) selected → parent stays
    if (nextChildren.length > 0) {
      promptHadChildrenRef.current[activeGroup.key] = true;
      return;
    }

    // After child was used: keep parent while remnant remains (I'm shopping for / myself leftover)
    // Do NOT fall back to exact-starter check on the next keystroke after child deselects.
    if (promptHadChildrenRef.current[activeGroup.key]) {
      if (!hasParentRemnantLeft(nextIntent, activeGroup)) {
        setActivePromptKey("");
        activePromptKeyRef.current = "";
        promptHadChildrenRef.current[activeGroup.key] = false;
      }
      return;
    }

    // Point 1: true parent-only (never had child) — any edit of starter → deselect parent
    if (!isExactParentPromptText(nextIntent, activeGroup)) {
      setActivePromptKey("");
      activePromptKeyRef.current = "";
    }
  };

  const clearChipHoverLeaveTimer = () => {
    if (chipHoverLeaveTimerRef.current) {
      clearTimeout(chipHoverLeaveTimerRef.current);
      chipHoverLeaveTimerRef.current = null;
    }
  };

  const handleParentChipMouseEnter = (group) => {
    clearChipHoverLeaveTimer();
    setHoveredPromptLabel(group.starter);
    setHoveredParentGhost(group.starter);
    setHoveredChildKey("");
  };

  const handleParentChipMouseLeave = () => {
    clearChipHoverLeaveTimer();
    chipHoverLeaveTimerRef.current = setTimeout(() => {
      setHoveredPromptLabel("");
      setHoveredParentGhost("");
      chipHoverLeaveTimerRef.current = null;
    }, 120);
  };

  const handleChildChipMouseEnter = (child) => {
    clearChipHoverLeaveTimer();
    setHoveredParentGhost("");
    setHoveredPromptLabel("");
    setHoveredChildKey(child);
  };

  const handleChildChipMouseLeave = () => {
    clearChipHoverLeaveTimer();
    chipHoverLeaveTimerRef.current = setTimeout(() => {
      setHoveredChildKey("");
      chipHoverLeaveTimerRef.current = null;
    }, 120);
  };

  const handlePromptChip = (group) => {
    clearChipHoverLeaveTimer();
    setMode("discover");
    setHoveredChildKey("");
    setHoveredParentGhost("");
    setHoveredPromptLabel("");
    const isSameActive = activePromptKey === group.key;
    const previousChildren = selectedPromptChildren[group.key] || [];

    if (isSameActive) {
      setActivePromptKey("");
      activePromptKeyRef.current = "";
      promptHadChildrenRef.current[group.key] = false;
      const nextMap = { ...selectedPromptChildrenRef.current, [group.key]: [] };
      selectedPromptChildrenRef.current = nextMap;
      setSelectedPromptChildren(nextMap);
      setIntent((prev) => {
        const next = removeGroupSentenceCompletely(prev, group, previousChildren);
        lastChipIntentRef.current = next;
        prevDiscoverIntentRef.current = next;
        return next;
      });
      setSearchAccentBorder(false);
      setSearchStartExpanded(true);
      setSearchViewResetKey((n) => n + 1);
      return;
    }

    setActivePromptKey(group.key);
    activePromptKeyRef.current = group.key;
    promptHadChildrenRef.current[group.key] = false;
    const nextMap = { ...selectedPromptChildrenRef.current, [group.key]: [] };
    selectedPromptChildrenRef.current = nextMap;
    setSelectedPromptChildren(nextMap);
    setIntent((prev) => {
      // Re-selecting a parent that already has a completed sentence should APPEND a
      // fresh starter (keep writing) instead of replacing the existing sentence.
      // e.g. "...myself and my mother. ...ring and bracelet." + "I'm shopping for..."
      const shouldAppendNewSentence = hasCompletedGroupSentenceInIntent(
        prev,
        group,
        previousChildren
      );
      const next = shouldAppendNewSentence
        ? appendPromptSegment(String(prev || "").trim(), group.starter)
        : upsertGroupSentence(prev, group, []);
      lastChipIntentRef.current = next;
      prevDiscoverIntentRef.current = next;
      return next;
    });
    setSearchAccentBorder(true);
    setSearchStartExpanded(true);
    setSearchViewResetKey((n) => n + 1);
   
  };

  const handleChildPromptChip = (group, child) => {
    clearChipHoverLeaveTimer();
    setMode("discover");
    setActivePromptKey(group.key);
    activePromptKeyRef.current = group.key;
    setHoveredChildKey("");
    setHoveredParentGhost("");
    setHoveredPromptLabel("");
    const previousChildren = selectedPromptChildrenRef.current[group.key] || [];
    const isSelected = previousChildren.includes(child);
    const nextChildren = isSelected
      ? previousChildren.filter((entry) => entry !== child)
      : [...previousChildren, child];

    const nextMap = {
      ...selectedPromptChildrenRef.current,
      [group.key]: nextChildren,
    };
    selectedPromptChildrenRef.current = nextMap;
    setSelectedPromptChildren(nextMap);
    // Track that this group used children (for remnant-based parent keep after text edit)
    promptHadChildrenRef.current[group.key] = nextChildren.length > 0;

    // Point 5: rebuild full clean sentence
    // Point 6: Fiancée/Fiancé — old segment removed completely via upsert
    setIntent((prev) => {
      const next = upsertGroupSentence(prev, group, nextChildren, previousChildren);
      lastChipIntentRef.current = next;
      prevDiscoverIntentRef.current = next;
      return next;
    });
    setSearchAccentBorder(true);
    setSearchStartExpanded(true);
    setSearchViewResetKey((n) => n + 1);
  };

  const examples = mode === "search" ? SEARCH_EXAMPLES : DISCOVER_EXAMPLES;
  const activePromptGroup = DISCOVER_PROMPT_GROUPS.find(
    (group) => group.key === activePromptKey
  );
  const isDiscoverMode = mode === "discover";
  const activeChildren = activePromptGroup
    ? selectedPromptChildren[activePromptGroup.key] || []
    : [];
  // Parent keeps "..." in the field. Child hover: gray word fills the "..." slot.
  // Style: "I prefer a... style." → "I prefer a " + [modern] + " style."
  // If 1+ children already selected, hovering another child previews the rebuilt sentence.
  const parentParts = activePromptGroup
    ? getParentPromptParts(activePromptGroup)
    : { prefix: "", suffix: "" };
  const intentText = String(intent || "");
  const parentStarterOnly =
    Boolean(activePromptGroup) &&
    activeChildren.length === 0 &&
    (intentText.trim() === String(activePromptGroup.starter || "").trim() ||
      intentText.endsWith(activePromptGroup.starter));

  let discoverFieldValue = intentText;
  let childGhostSuffix = "";
  let childGhostAfter = "";

  if (activePromptGroup && hoveredChildKey) {
    const hoveringSelected = activeChildren.includes(hoveredChildKey);
    const { prefix, suffix } = parentParts;
    const phrase = getChildPhrase(activePromptGroup, hoveredChildKey);

    if (!hoveringSelected && parentStarterOnly) {
      // No child selected yet — gray fills the "..." slot
      if (intentText.trim() === String(activePromptGroup.starter || "").trim()) {
        discoverFieldValue = prefix;
      } else if (intentText.endsWith(activePromptGroup.starter)) {
        discoverFieldValue =
          intentText.slice(0, -activePromptGroup.starter.length) + prefix;
      }
      childGhostSuffix = phrase ? (suffix ? phrase : `${phrase}.`) : "";
      childGhostAfter = suffix;
    } else if (!hoveringSelected && activeChildren.length >= 1 && phrase) {
      // Keep already-selected children as solid text.
      // Do NOT rebuild via common-prefix ghost — that greys selected chips
      // (e.g. "earrings") when "and"/comma shifts for the new item.
      discoverFieldValue = intentText;
      const trimmed = intentText.trim();
      if (/[.!?]$/.test(trimmed)) {
        discoverFieldValue = trimmed.replace(/[.!?]+$/, "");
        childGhostSuffix =
          activeChildren.length === 1
            ? ` and ${phrase}`
            : `, and ${phrase}`;
        childGhostAfter = ".";
      } else {
        childGhostSuffix = `${/\s$/.test(intentText) ? "" : " "}${phrase}`;
        childGhostAfter = "";
      }
    }
  }

  // Empty field: parent hover uses normal placeholder (unchanged).
  // Field has text: parent hover shows starter as gray ghost (like child chips).
  const parentHoverGhost =
    Boolean(intentText.trim()) &&
    Boolean(hoveredParentGhost) &&
    !hoveredChildKey &&
    !intentText.includes(hoveredParentGhost)
      ? `${/\s$/.test(intentText) ? "" : " "}${hoveredParentGhost}`
      : "";

  const fieldGhostSuffix = childGhostSuffix || parentHoverGhost;
  const fieldGhostAfter = childGhostAfter;
  const hasSearchInput = isDiscoverMode
    ? Boolean(intent.trim())
    : Boolean(queryText.trim() || imageData);
  const showClearControl = isDiscoverMode || hasSearchInput;
  const showImageQuickview = Boolean(imageData) && !isDiscoverMode;

  // Discover Discovery visibility (normal search always has entry button)
  const showDiscoverButton =
    !isDiscoverMode ||
    discoverExitStep === 2 ||
    (discoverExitStep === 0 && hasSearchInput && !isSearchFieldExpanded);

  // When Discovery is visible in discover → camera (not cross). Step 1 → cross only.
  const showDiscoverCamera = isDiscoverMode && showDiscoverButton;
  const showDiscoverCross =
    isDiscoverMode &&
    !showDiscoverButton &&
    (discoverExitStep === 1 || showClearControl);

  const clearSearchInput = () => {
    // After 1st Discovery clear (X + mic only): X → normal search field on same page
    if (isDiscoverMode && discoverExitStep === 1) {
      setMode("search");
      setIntent("");
      setQueryText("");
      setActivePromptKey("");
      setSelectedPromptChildren({});
      selectedPromptChildrenRef.current = {};
      activePromptKeyRef.current = "";
      promptHadChildrenRef.current = {};
      lastChipIntentRef.current = "";
      setDiscoverExitStep(0);
      setSearchAccentBorder(false);
      // Keep hasSearched + results — do not leave this page
      setSearchStartExpanded(true);
      setSearchViewResetKey((n) => n + 1);
      return;
    }

    if (isDiscoverMode) {
      if (intent.trim()) {
        setIntent("");
        setActivePromptKey("");
        setSelectedPromptChildren({});
        selectedPromptChildrenRef.current = {};
        activePromptKeyRef.current = "";
        promptHadChildrenRef.current = {};
        lastChipIntentRef.current = "";
        setSearchAccentBorder(false);
      } else {
        setMode("search");
      }
    } else {
      setQueryText("");
      clearImage();
    }
    setDiscoverExitStep(0);
    setHasSearched(false);
    resetResults();
    setSearchStartExpanded(true);
    setSearchViewResetKey((n) => n + 1);
  };

  const handleDiscoveryClick = () => {
    // From normal search → enter discover with empty input (do not carry query/image)
    if (mode !== "discover") {
      setMode("discover");
      setIntent("");
      setQueryText("");
      setActivePromptKey("");
      setSelectedPromptChildren({});
      selectedPromptChildrenRef.current = {};
      activePromptKeyRef.current = "";
      promptHadChildrenRef.current = {};
      lastChipIntentRef.current = "";
      prevDiscoverIntentRef.current = "";
      setSearchAccentBorder(false);
      clearImage();
      setHasSearched(false);
      resetResults();
      setDiscoverExitStep(0);
      setSearchStartExpanded(true);
      setSearchViewResetKey((n) => n + 1);
      return;
    }

    // 2nd Discovery click (after X restored Discovery): open prompt chip page
    if (discoverExitStep === 2) {
      setIntent("");
      setActivePromptKey("");
      setSelectedPromptChildren({});
      selectedPromptChildrenRef.current = {};
      activePromptKeyRef.current = "";
      promptHadChildrenRef.current = {};
      lastChipIntentRef.current = "";
      setHasSearched(false);
      resetResults();
      setDiscoverExitStep(0);
      setSearchAccentBorder(false);
      setSearchStartExpanded(true);
      setSearchViewResetKey((n) => n + 1);
      return;
    }

    // Results page, 1st Discovery click: clear field, hide Discovery → X + mic only
    if (hasSearched && intent.trim()) {
      setIntent("");
      setActivePromptKey("");
      setSelectedPromptChildren({});
      selectedPromptChildrenRef.current = {};
      activePromptKeyRef.current = "";
      promptHadChildrenRef.current = {};
      lastChipIntentRef.current = "";
      setSearchAccentBorder(false);
      setDiscoverExitStep(1);
      setSearchStartExpanded(true);
      setSearchViewResetKey((n) => n + 1);
      return;
    }

    // Fallback: open prompt chip page
    setIntent("");
    setActivePromptKey("");
    setSelectedPromptChildren({});
    selectedPromptChildrenRef.current = {};
    activePromptKeyRef.current = "";
    promptHadChildrenRef.current = {};
    lastChipIntentRef.current = "";
    setHasSearched(false);
    resetResults();
    setDiscoverExitStep(0);
    setSearchAccentBorder(false);
    setSearchStartExpanded(true);
    setSearchViewResetKey((n) => n + 1);
  };

  const navigateAfterClose = () => {
    if (isFromQuickview) {
      persistFaroSession();
      navigate("/app/pos/Quickview", {
        state: routeState?.quickviewState || {},
      });
      return;
    }
    if (isFromPosscreen) {
      persistFaroSession();
      navigate("/app/pos/posscreen");
      return;
    }
    if (isFromProducts) {
      persistFaroSession();
      navigate("/app/pos/products", {
        state: routeState?.productsState || {
          Optiondetails: routeState?.filtertype,
        },
      });
      return;
    }
    persistFaroSession();
    navigate("/app/pos/menu");
  };

  const handleCloseFaro = () => {
    if (isClosing) return;
    // Ensure reopen-from-icon restores the latest phase after minimize.
    persistFaroSession();
    setIsClosing(true);
    if (closeNavTimeoutRef.current) {
      clearTimeout(closeNavTimeoutRef.current);
    }
    // Fallback if animationend doesn't fire
    closeNavTimeoutRef.current = setTimeout(() => {
      navigateAfterClose();
    }, 120);
  };

  const handleCloseAnimationEnd = (event) => {
    if (!isClosing) return;
    if (event.target !== event.currentTarget) return;
    if (closeNavTimeoutRef.current) {
      clearTimeout(closeNavTimeoutRef.current);
      closeNavTimeoutRef.current = null;
    }
    navigateAfterClose();
  };

  const pageHeader = (
    <header
      className={`scs__page-header${
        hasSearched ? " scs__page-header--results" : ""
      }`}
    >
      <div className="scs__breadcrumb">
        <span style={{cursor:'default'}} className="scs__breadcrumb-title" onClick={()=>{clearSearchInput();setMode("search")}}>
          {/* <img src={faromainicon} alt="" aria-hidden />
          <span>{t("faro")}</span> */}
          <svg xmlns="http://www.w3.org/2000/svg" width="50" height="24" viewBox="0 0 50 24" fill="none">
  <path d="M44.3071 9.96073C44.3661 9.8002 44.5942 9.8002 44.6531 9.96073L44.8294 10.4399C44.9676 10.8158 45.1864 11.1571 45.4707 11.4402C45.7549 11.7232 46.0978 11.9411 46.4753 12.0787L46.9565 12.2542C47.118 12.3129 47.118 12.54 46.9565 12.5987L46.4753 12.7742C46.0978 12.9118 45.7549 13.1297 45.4707 13.4127C45.1864 13.6958 44.9676 14.0371 44.8294 14.413L44.6531 14.8922C44.5942 15.0529 44.3661 15.0529 44.3071 14.8922L44.1309 14.413C43.9927 14.0371 43.7738 13.6958 43.4896 13.4127C43.2053 13.1297 42.8625 12.9118 42.485 12.7742L42.0037 12.5987C41.8425 12.54 41.8425 12.3129 42.0037 12.2542L42.485 12.0787C42.8625 11.9411 43.2053 11.7232 43.4896 11.4402C43.7738 11.1571 43.9927 10.8158 44.1309 10.4399L44.3071 9.96073Z" fill="url(#paint0_linear_3558_4815)"/>
  <path d="M46.3642 8.91575C46.3863 8.85556 46.4718 8.85556 46.494 8.91575L46.56 9.09543C46.6119 9.23639 46.6939 9.3644 46.8005 9.47054C46.9071 9.57668 47.0357 9.6584 47.1773 9.71L47.3577 9.77579C47.4183 9.79782 47.4183 9.88298 47.3577 9.905L47.1773 9.9708C47.0357 10.0224 46.9071 10.1041 46.8005 10.2103C46.6939 10.3164 46.6119 10.4444 46.56 10.5854L46.494 10.765C46.4718 10.8253 46.3863 10.8253 46.3642 10.765L46.2981 10.5854C46.2463 10.4444 46.1642 10.3164 46.0576 10.2103C45.951 10.1041 45.8224 10.0224 45.6809 9.9708L45.5004 9.905C45.44 9.88298 45.44 9.79782 45.5004 9.77579L45.6809 9.71C45.8224 9.6584 45.951 9.57668 46.0576 9.47054C46.1642 9.3644 46.2463 9.23639 46.2981 9.09543L46.3642 8.91575Z" fill="url(#paint1_linear_3558_4815)"/>
  <path d="M5.41584 20.4178C5.41584 20.5213 5.45047 20.6075 5.51973 20.6765C5.589 20.7368 5.68857 20.7756 5.81844 20.7928L7.38986 20.9221C7.45913 20.9308 7.50675 20.948 7.53272 20.9739C7.56735 20.9911 7.58467 21.017 7.58467 21.0515C7.58467 21.1204 7.53272 21.1549 7.42883 21.1549H3.16908C3.13445 21.1549 3.10415 21.1463 3.07818 21.129C3.0522 21.1032 3.03921 21.0773 3.03921 21.0515C3.03921 21.017 3.05653 20.9911 3.09116 20.9739C3.12579 20.948 3.16908 20.9351 3.22103 20.9351L4.24701 20.7928C4.37688 20.767 4.47644 20.7239 4.54571 20.6635C4.61497 20.6032 4.6496 20.5299 4.6496 20.4437V10.7066C4.6496 10.5946 4.62363 10.5084 4.57168 10.448C4.52839 10.379 4.44614 10.3359 4.32493 10.3187L2.81844 10.1118C2.73186 10.0946 2.67125 10.0773 2.63662 10.0601C2.61064 10.0342 2.59766 10.004 2.59766 9.96956C2.59766 9.93508 2.60631 9.91352 2.62363 9.9049C2.6496 9.88766 2.67991 9.87904 2.71454 9.87904H4.83142L4.68857 10.0601V9.68508C4.68857 9.33163 4.63229 8.99111 4.51973 8.66352C4.40718 8.32732 4.27731 7.99111 4.13012 7.6549C3.98294 7.3187 3.85307 6.96525 3.74051 6.59456C3.63662 6.21525 3.58467 5.81008 3.58467 5.37904C3.58467 4.56008 3.84008 3.89197 4.3509 3.37473C4.87038 2.84887 5.51541 2.58594 6.28597 2.58594C6.76216 2.58594 7.16909 2.66783 7.50675 2.83163C7.85307 2.99542 8.11714 3.21525 8.29896 3.49111C8.48077 3.76697 8.57168 4.06008 8.57168 4.37042C8.57168 4.6118 8.51973 4.79714 8.41584 4.92645C8.3206 5.05577 8.19506 5.12042 8.03921 5.12042C7.89203 5.12042 7.76649 5.0687 7.66259 4.96525C7.5587 4.85318 7.50675 4.68939 7.50675 4.47387V4.06008C7.50675 3.66352 7.39419 3.35318 7.16908 3.12904C6.95263 2.9049 6.61497 2.79283 6.1561 2.79283C5.52406 2.80146 5.01757 3.00835 4.63662 3.41352C4.26432 3.81008 4.07818 4.35318 4.07818 5.04283C4.07818 5.37042 4.12147 5.69801 4.20805 6.02559C4.29463 6.34456 4.40285 6.65921 4.53272 6.96956C4.67125 7.27128 4.80545 7.57301 4.93532 7.87473C5.07385 8.17645 5.1864 8.47818 5.27298 8.7799C5.36822 9.08163 5.41584 9.38335 5.41584 9.68508V10.0213L5.28597 9.87904H7.97428C8.03489 9.87904 8.07385 9.88766 8.09116 9.9049C8.11714 9.91352 8.13012 9.93508 8.13012 9.96956C8.13012 10.0213 8.10848 10.0644 8.06519 10.0989C8.0219 10.1247 7.95263 10.1377 7.8574 10.1377H5.41584V20.4178Z" fill="black"/>
  <path d="M16.3805 19.8747V19.7971L16.3675 19.7584V13.0601C16.3675 11.9997 16.1511 11.198 15.7182 10.6549C15.2853 10.1032 14.623 9.82732 13.7312 9.82732C12.8134 9.82732 12.1468 10.0256 11.7312 10.4221C11.3156 10.8187 11.1078 11.2282 11.1078 11.6506V12.2713C11.1078 12.4782 11.0515 12.629 10.939 12.7239C10.8351 12.8101 10.7095 12.8532 10.5623 12.8532C10.4152 12.8532 10.2896 12.7928 10.1857 12.6721C10.0905 12.5515 10.0429 12.3877 10.0429 12.1808C10.0429 11.7411 10.1944 11.3273 10.4974 10.9394C10.8004 10.5428 11.2333 10.2239 11.7961 9.98249C12.3675 9.74111 13.0472 9.62042 13.8351 9.62042C14.952 9.62042 15.7788 9.89628 16.3156 10.448C16.861 10.9997 17.1338 11.7368 17.1338 12.6592V19.9523C17.1338 20.3747 17.1944 20.6721 17.3156 20.8446C17.4455 21.0084 17.61 21.0902 17.8091 21.0902C18.0169 21.0902 18.1771 21.0428 18.2896 20.948C18.4022 20.8532 18.4758 20.7368 18.5104 20.5989C18.5191 20.573 18.532 20.5515 18.5494 20.5342C18.5667 20.517 18.5883 20.5084 18.6143 20.5084C18.6403 20.5084 18.6576 20.517 18.6662 20.5342C18.6836 20.5515 18.6922 20.5773 18.6922 20.6118C18.6922 20.7325 18.6533 20.8532 18.5753 20.9739C18.4974 21.0859 18.3849 21.1765 18.2377 21.2454C18.0905 21.323 17.9087 21.3618 17.6922 21.3618C17.2766 21.3618 16.952 21.2411 16.7182 20.9997C16.4931 20.7497 16.3805 20.3747 16.3805 19.8747ZM9.47144 18.6721C9.47144 17.8532 9.81776 17.1592 10.5104 16.5902C11.203 16.0213 12.1684 15.7368 13.4065 15.7368C14.1251 15.7368 14.7485 15.8058 15.2766 15.9437C15.8134 16.0816 16.3069 16.2411 16.7572 16.4221L16.7052 16.6032C16.2636 16.4308 15.7918 16.2799 15.2896 16.1506C14.7961 16.0127 14.2507 15.9437 13.6533 15.9437C12.571 15.9437 11.7485 16.1894 11.1857 16.6808C10.623 17.1635 10.3416 17.7842 10.3416 18.5428C10.3416 19.2842 10.584 19.8834 11.0688 20.3402C11.5623 20.7971 12.2074 21.0256 13.0039 21.0256C13.6619 21.0256 14.2983 20.8963 14.913 20.6377C15.5364 20.379 16.0732 19.9825 16.5234 19.448L16.6533 19.5773C16.1857 20.1549 15.6013 20.5989 14.9 20.9092C14.1987 21.2196 13.4541 21.3747 12.6662 21.3747C11.6965 21.3747 10.9217 21.1334 10.3416 20.6506C9.76148 20.1592 9.47144 19.4997 9.47144 18.6721Z" fill="black"/>
  <path d="M22.2734 15.2325C22.2734 14.017 22.4379 12.9954 22.7669 12.1678C23.0959 11.3316 23.5158 10.7023 24.0266 10.2799C24.5461 9.84887 25.0829 9.63335 25.637 9.63335C26.2344 9.63335 26.6976 9.79283 27.0266 10.1118C27.3643 10.4308 27.5331 10.8187 27.5331 11.2756C27.5331 11.4997 27.4812 11.6765 27.3773 11.8058C27.282 11.9265 27.1608 11.9868 27.0136 11.9868C26.8665 11.9868 26.7366 11.9394 26.624 11.8446C26.5201 11.7411 26.4682 11.5816 26.4682 11.3661V11.0558C26.4682 10.6851 26.3859 10.4178 26.2214 10.254C26.0656 10.0902 25.7972 10.0084 25.4162 10.0084C24.9574 10.0084 24.4985 10.2109 24.0396 10.6161C23.5894 11.0213 23.2128 11.6161 22.9097 12.4006C22.6067 13.1765 22.4552 14.1334 22.4552 15.2713L22.2734 15.2325ZM22.4552 9.95663V20.4178C22.4552 20.5213 22.4898 20.6075 22.5591 20.6765C22.6284 20.7368 22.7279 20.7756 22.8578 20.7928L24.1565 20.9221C24.2258 20.9308 24.2734 20.948 24.2994 20.9739C24.3253 20.9911 24.3383 21.017 24.3383 21.0515C24.3383 21.0773 24.3253 21.1032 24.2994 21.129C24.2734 21.1463 24.2344 21.1549 24.1825 21.1549H20.2474C20.2041 21.1549 20.1652 21.1463 20.1305 21.129C20.0959 21.1032 20.0786 21.0773 20.0786 21.0515C20.0786 21.017 20.0959 20.9911 20.1305 20.9739C20.1652 20.948 20.2214 20.9308 20.2994 20.9221L21.2864 20.7928C21.4162 20.7756 21.5158 20.7368 21.5851 20.6765C21.6543 20.6075 21.689 20.5299 21.689 20.4437V10.7842C21.689 10.7239 21.676 10.6765 21.65 10.642C21.624 10.6075 21.5721 10.5902 21.4942 10.5902H20.1695C20.1262 10.5902 20.0916 10.5816 20.0656 10.5644C20.0396 10.5471 20.0266 10.5256 20.0266 10.4997C20.0266 10.4739 20.0353 10.4523 20.0526 10.4351C20.0786 10.4092 20.1175 10.3877 20.1695 10.3704L22.0136 9.84025C22.0916 9.81439 22.1522 9.79714 22.1955 9.78852C22.2388 9.7799 22.2734 9.77559 22.2994 9.77559C22.3513 9.77559 22.3903 9.79283 22.4162 9.82732C22.4422 9.8618 22.4552 9.9049 22.4552 9.95663Z" fill="black"/>
  <path d="M34.1839 9.64628C35.2229 9.64628 36.1493 9.89628 36.9631 10.3963C37.777 10.8963 38.4177 11.5902 38.8852 12.4782C39.3614 13.3661 39.5995 14.392 39.5995 15.5558C39.5995 16.7109 39.3614 17.7282 38.8852 18.6075C38.4177 19.4868 37.7726 20.1765 36.9501 20.6765C36.1276 21.1678 35.1839 21.4135 34.119 21.4135C33.08 21.4135 32.1536 21.1678 31.3397 20.6765C30.5345 20.1765 29.9025 19.4868 29.4436 18.6075C28.9848 17.7282 28.7553 16.7109 28.7553 15.5558C28.7553 14.392 28.9804 13.3661 29.4307 12.4782C29.8809 11.5902 30.5129 10.8963 31.3268 10.3963C32.1406 9.89628 33.093 9.64628 34.1839 9.64628ZM34.7943 21.1161C35.6947 21.0385 36.461 20.7411 37.093 20.2239C37.725 19.7066 38.1882 19.0084 38.4826 18.129C38.777 17.2497 38.8679 16.2325 38.7553 15.0773C38.6514 13.8877 38.3657 12.8963 37.8982 12.1032C37.4306 11.3015 36.8246 10.7152 36.08 10.3446C35.3354 9.97387 34.4913 9.82732 33.5475 9.9049C32.6038 9.99111 31.8159 10.3015 31.1839 10.8359C30.5605 11.3704 30.1103 12.0773 29.8333 12.9566C29.5562 13.8359 29.4696 14.8402 29.5735 15.9696C29.6861 17.1075 29.9804 18.0773 30.4566 18.879C30.9328 19.6808 31.5432 20.2756 32.2878 20.6635C33.041 21.0428 33.8765 21.1937 34.7943 21.1161Z" fill="black"/>
  <path d="M41.6886 19.3963H43.6334L43.6561 19.5967H41.6496L41.6886 19.3963ZM41.9191 21.0741C41.9191 21.0978 41.9093 21.1172 41.8899 21.1323C41.8704 21.1474 41.839 21.1549 41.7957 21.1549H40.8087C40.7676 21.1549 40.7362 21.1484 40.7145 21.1355C40.6951 21.1204 40.6853 21.101 40.6853 21.0773C40.6853 21.0579 40.6918 21.0418 40.7048 21.0288C40.72 21.0159 40.7492 21.0019 40.7925 20.9868L40.9386 20.9448C40.997 20.9254 41.0403 20.8963 41.0684 20.8575C41.0966 20.8165 41.129 20.7422 41.1658 20.6344L42.4061 16.9911C42.4277 16.9308 42.431 16.8877 42.4158 16.8618C42.4007 16.8359 42.3541 16.8155 42.2762 16.8004C42.22 16.7896 42.1799 16.7767 42.1561 16.7616C42.1323 16.7465 42.1204 16.7282 42.1204 16.7066C42.1204 16.6808 42.1312 16.6614 42.1529 16.6484C42.1745 16.6355 42.2059 16.629 42.247 16.629H43.3314C43.3747 16.629 43.4061 16.6355 43.4256 16.6484C43.4451 16.6614 43.4548 16.6797 43.4548 16.7034C43.4548 16.7271 43.444 16.7465 43.4223 16.7616C43.4007 16.7767 43.3617 16.7896 43.3054 16.8004C43.2405 16.809 43.2015 16.8241 43.1886 16.8456C43.1756 16.8672 43.1777 16.9027 43.1951 16.9523L44.5035 20.7476C44.5251 20.8187 44.5565 20.8715 44.5977 20.906C44.6409 20.9405 44.7037 20.9642 44.786 20.9771C44.8379 20.9879 44.8725 21.0008 44.8899 21.0159C44.9072 21.031 44.9158 21.0504 44.9158 21.0741C44.9158 21.0978 44.905 21.1172 44.8834 21.1323C44.8639 21.1474 44.8336 21.1549 44.7925 21.1549H43.6496C43.6106 21.1549 43.5803 21.1474 43.5587 21.1323C43.5371 21.1172 43.5262 21.0978 43.5262 21.0741C43.5262 21.0525 43.5349 21.0342 43.5522 21.0191C43.5695 21.004 43.5977 20.9933 43.6366 20.9868L43.9029 20.9577C43.9591 20.9491 43.9894 20.9286 43.9938 20.8963C43.9981 20.864 43.9883 20.8122 43.9645 20.7411L42.6269 16.8359L42.708 16.8101L41.4158 20.6183C41.3942 20.6872 41.3834 20.7433 41.3834 20.7864C41.3834 20.8295 41.3974 20.864 41.4256 20.8898C41.4559 20.9157 41.4992 20.9362 41.5554 20.9512L41.8087 20.99C41.8477 20.9965 41.8758 21.0062 41.8931 21.0191C41.9104 21.0321 41.9191 21.0504 41.9191 21.0741Z" fill="black"/>
  <path d="M46.4197 20.7864C46.4197 20.8295 46.4327 20.864 46.4587 20.8898C46.4868 20.9157 46.5258 20.9329 46.5755 20.9415L46.8255 20.9803C46.8948 20.9954 46.9294 21.0267 46.9294 21.0741C46.9294 21.0978 46.9208 21.1172 46.9035 21.1323C46.8861 21.1474 46.8569 21.1549 46.8158 21.1549H45.5203C45.4814 21.1549 45.4522 21.1474 45.4327 21.1323C45.4154 21.1172 45.4067 21.0978 45.4067 21.0741C45.4067 21.0267 45.4413 20.9954 45.5106 20.9803L45.7574 20.9415C45.8071 20.9329 45.8461 20.9157 45.8742 20.8898C45.9024 20.864 45.9164 20.8295 45.9164 20.7864V16.9976C45.9164 16.9545 45.9024 16.92 45.8742 16.8941C45.8461 16.8683 45.8071 16.851 45.7574 16.8424L45.5106 16.8036C45.4413 16.7885 45.4067 16.7573 45.4067 16.7099C45.4067 16.684 45.4154 16.6646 45.4327 16.6517C45.4522 16.6366 45.4814 16.629 45.5203 16.629H46.8158C46.8569 16.629 46.8861 16.6366 46.9035 16.6517C46.9208 16.6646 46.9294 16.684 46.9294 16.7099C46.9294 16.7573 46.8948 16.7885 46.8255 16.8036L46.5755 16.8424C46.5258 16.851 46.4868 16.8683 46.4587 16.8941C46.4327 16.92 46.4197 16.9545 46.4197 16.9976V20.7864Z" fill="black"/>
  <defs>
    <linearGradient id="paint0_linear_3558_4815" x1="44.4802" y1="9.84033" x2="44.4802" y2="15.0127" gradientUnits="userSpaceOnUse">
      <stop stop-color="#6E78FF"/>
      <stop offset="1" stop-color="#61F2DE"/>
    </linearGradient>
    <linearGradient id="paint1_linear_3558_4815" x1="46.4291" y1="8.87061" x2="46.4291" y2="10.8103" gradientUnits="userSpaceOnUse">
      <stop stop-color="#6E78FF"/>
      <stop offset="1" stop-color="#61F2DE"/>
    </linearGradient>
  </defs>
</svg>
        </span>
        <button
          type="button"
          className="scs__minimize"
          aria-label={t("Close FARO")}
          onClick={handleCloseFaro}
        >
          <span aria-hidden />
        </button>
      </div>
    </header>
  );

  return (
    <div className="main-body scs-page">
      <div className="main-body-row">
        <div className="main-body-row-left-col p-0">
          <div
            className={`scs${hasSearched ? " scs--results" : ""}${
              isOrderOpen ? " scs--order-open" : ""
            }${isClosing ? " scs--closing" : ""}`}
            onAnimationEnd={handleCloseAnimationEnd}
          >
            {!hasSearched ? pageHeader : null}

            {hasSearched && cartBadgeCount > 0 && !isOrderOpen && !isFromQuickview ? (
              <FloatingSearchButton
                count={cartBadgeCount}
                t={t}
                onClick={() => {
                  if (orderItems.length) {
                    setIsOrderOpen(true);
                    return;
                  }
                  // Confirmed into POS cart already → open POS screen cart
                  if (isFromPosscreen && posConfirmedCount > 0) {
                    persistFaroSession();
                    navigate("/app/pos/posscreen");
                  }
                }}
              />
            ) : null}

            <main className={`scs__quickview${hasSearched ? " scs__quickview--compact" : ""}`}>
              {hasSearched && !isOrderOpen ? pageHeader : null}
              {!hasSearched ? (
                <section
                  className={`scs__heading${
                    isDiscoverMode ? " scs__heading--discover" : ""
                  }`}
                >
                  {isDiscoverMode ? (
                    <div className="scs__discover-brand">
                      <div className="scs__discover-wordmark">
                        <img src={faromainicon} alt="" aria-hidden />
                        <span>{t("faro")}</span>
                      </div>
                      <p>
                        {t(
                          "Ask faro about your style, occasion, budget, or preferences, or use the suggestions below to get started."
                        )}
                      </p>
                    </div>
                  ) : (
                    <>
                      <img className="scs__hero-mark" src={faromainicon} alt="" aria-hidden />
                      <div className="scs__heading-copy">
                        <h1>{t("Ask faro anything")}</h1>
                        <p>
                          {t(
                            "Use Quick Search for instant results, or tell faro AI about your ideal jewelry to receive personalized recommendations."
                          )}
                        </p>
                      </div>
                    </>
                  )}
                </section>
              ) : null}

              {!isOrderOpen ? (
              <section
                className={`scs__searchbox-container${
                  hasSearched ? " scs__searchbox-container--results" : ""
                }`}
              >
                <form
                  className={`scs__searchbox-input${
                    showImageQuickview
                      ? ` scs__searchbox-input--image${
                          !isImagePreviewExpanded
                            ? " scs__searchbox-input--image-collapsed"
                            : ""
                        }`
                      : ""
                  }`}
                  onSubmit={handleSubmit}
                >
                  <span className="scs__glow scs__glow--mint" aria-hidden />
                  <span className="scs__glow scs__glow--violet" aria-hidden />

                  <div
                    ref={searchbarRef}
                    className={`scs__searchbar${
                      hasSearchInput && !hasSearched ? " scs__searchbar--filled" : ""
                    }${isDiscoverMode ? " scs__searchbar--discover" : ""}${
                      searchAccentBorder ? " scs__searchbar--accent-border" : ""
                    }${hasSearched ? " scs__searchbar--results" : ""}${
                      showImageQuickview
                        ? ` scs__searchbar--image-quickview${
                            !isImagePreviewExpanded
                              ? " scs__searchbar--image-collapsed"
                              : ""
                          }`
                        : ""
                    }`}
                    onFocusCapture={activateSearchAccentBorder}
                  >
                    {showImageQuickview ? (
                      isImagePreviewExpanded ? (
                        <>
                          <div className="scs__searchbar-top-row">
                            <button
                              type="button"
                              className="scs__cmd-thumb"
                              title={t("Hide image preview")}
                              aria-label={t("Hide image preview")}
                              onClick={toggleImagePreview}
                            >
                              <img src={imageData} alt={imageName} />
                            </button>

                            <SearchbarTextField
                              textareaRef={searchTextRef}
                              value={queryText}
                              onChange={(e) => {
                                setMode("search");
                                setQueryText(e.target.value);
                              }}
                              onKeyDown={handleSearchKeyDown}
                              placeholder={
                                hasSearched
                                  ? t("Add to your search")
                                  : t("Search (Product name, Color, Type,...)")
                              }
                              showVoiceWave={isVoiceRecording}
                              voiceBarLevels={voiceBarLevels}
                              viewResetKey={searchViewResetKey}
                              initialExpanded={searchStartExpanded}
                              truncateRequestKey={searchTruncateKey}
                              onExpandedChange={handleSearchExpandedChange}
                              onVisualExpandedChange={handleSearchVisualExpandedChange}
                            />

                            <div className="scs__search-actions scs__search-actions--quickview">
                              {!hasSearched && showClearControl ? (
                                <button
                                  type="button"
                                  className="scs__close-btn"
                                  aria-label={t("Clear search")}
                                  onClick={clearSearchInput}
                                >
                                  {/* <span aria-hidden /> */}
                                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M11 21L21 11M11 11L21 21" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>

                                </button>
                              ) : null}

                              {!hasSearched ? (
                                <div className="scs__discover-actions">
                                  <MicButton
                                    value={queryText}
                                    onChange={setQueryText}
                                    disabled={isLoading}
                                    t={t}
                                    onStatusChange={handleVoiceStatusChange}
                                    onAudioLevels={handleVoiceAudioLevels}
                                  />
                                  <button
                                    type="button"
                                    className="scs__discover-btn"
                                    onClick={handleDiscoveryClick}
                                    disabled={isLoading}
                                  >
                                    <img src={greyfaroicon} alt="" aria-hidden />
                                    <span>{t("Discovery")}</span>
                                  </button>
                                  {hasSearchInput ? (
                                    <button
                                      type="submit"
                                      className="scs__enter-btn"
                                      aria-label={t("Search")}
                                      disabled={isLoading}
                                    >
                                      <span aria-hidden />
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}

                              <button
                                type="button"
                                className="scs__hide-btn"
                                aria-label={t("Hide image preview")}
                                aria-expanded
                                onClick={collapseImagePreview}
                              >
                                <span
                                  className="scs__hide-chevron scs__hide-chevron--up"
                                  aria-hidden
                                />
                              </button>
                            </div>
                          </div>

                          <div
                            className="scs__image-preview"
                            role="button"
                            tabIndex={0}
                            aria-label={t("Hide image preview")}
                            onClick={toggleImagePreview}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                toggleImagePreview();
                              }
                            }}
                          >
                            <img src={imageData} alt={imageName} />
                          </div>
                        </>
                      ) : (
                        <div className="scs__searchbar-collapsed-row">
                          <div className="scs__searchbar-collapsed-main">
                            <div className="scs__image-attach">
                              <button
                                type="button"
                                className="scs__cmd-thumb scs__cmd-thumb--expand scs__cmd-thumb--collapsed"
                                aria-label={t("Show image preview")}
                                title={t("Show image preview")}
                                onClick={expandImagePreview}
                              >
                                <img src={imageData} alt={imageName} />
                              </button>

                              <button
                                type="button"
                                className="scs__close-btn scs__close-btn--inline"
                                aria-label={t("Remove image")}
                                onClick={clearImage}
                              >
                                <span aria-hidden />
                              </button>
                            </div>

                            <SearchbarTextField
                              textareaRef={searchTextRef}
                              value={queryText}
                              onChange={(e) => {
                                setMode("search");
                                setQueryText(e.target.value);
                              }}
                              onKeyDown={handleSearchKeyDown}
                              placeholder={t("Add to your search")}
                              showVoiceWave={isVoiceRecording}
                              voiceBarLevels={voiceBarLevels}
                              viewResetKey={searchViewResetKey}
                              initialExpanded={searchStartExpanded}
                              truncateRequestKey={searchTruncateKey}
                              onExpandedChange={handleSearchExpandedChange}
                              onVisualExpandedChange={handleSearchVisualExpandedChange}
                            />
                          </div>

                          <div className="scs__search-actions scs__search-actions--collapsed">
                            {!queryText.trim() ? (
                              <button
                                type="button"
                                className="scs__icon-btn"
                                title={imageName ? t("Change image") : t("Search with image")}
                                onClick={openImageUploadModal}
                              >
                                <img src={camiconuploadimage} alt="" aria-hidden />
                              </button>
                            ) : null}

                            <MicButton
                              value={queryText}
                              onChange={setQueryText}
                              disabled={isLoading}
                              t={t}
                              onStatusChange={handleVoiceStatusChange}
                              onAudioLevels={handleVoiceAudioLevels}
                            />

                            {queryText.trim() ? (
                              <button
                                type="submit"
                                className="scs__enter-btn"
                                aria-label={t("Search")}
                                disabled={isLoading}
                              >
                                <span aria-hidden />
                              </button>
                            ) : null}
                          </div>
                        </div>
                      )
                    ) : (
                      <>
                        <SearchbarTextField
                          textareaRef={searchTextRef}
                          value={isDiscoverMode ? discoverFieldValue : queryText}
                          onChange={(e) => {
                            if (isDiscoverMode) {
                              handleDiscoverIntentChange(e.target.value);
                            } else {
                              setMode("search");
                              setQueryText(e.target.value);
                            }
                          }}
                          onKeyDown={handleSearchKeyDown}
                          placeholder={
                            isDiscoverMode
                              ? !intent.trim() && hoveredPromptLabel
                                ? hoveredPromptLabel
                                : t("Describe the jewelry you're looking for...")
                              : t("Search (Product name, Color, Type,...)")
                          }
                          ghostSuffix={isDiscoverMode ? fieldGhostSuffix : ""}
                          ghostAfter={isDiscoverMode ? fieldGhostAfter : ""}
                          showVoiceWave={isVoiceRecording}
                          voiceBarLevels={voiceBarLevels}
                          viewResetKey={searchViewResetKey}
                          initialExpanded={searchStartExpanded}
                          truncateRequestKey={searchTruncateKey}
                          onExpandedChange={handleSearchExpandedChange}
                          onVisualExpandedChange={handleSearchVisualExpandedChange}
                        />

                        <div className="scs__search-actions">
                          {isDiscoverMode ? (
                            showDiscoverCamera ? (
                              <button
                                type="button"
                                className={`scs__icon-btn${isLoading ? " is-disabled" : ""}`}
                                title={imageName ? t("Change image") : t("Search with image")}
                                aria-disabled={isLoading || undefined}
                                disabled={isLoading}
                                onClick={openImageUploadModal}
                              >
                                <img src={camiconuploadimage} alt="" aria-hidden />
                              </button>
                            ) : showDiscoverCross ? (
                              <button
                                type="button"
                                className="scs__close-btn"
                                aria-label={t("Clear search")}
                                onClick={clearSearchInput}
                                disabled={isLoading}
                              >
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M11 21L21 11M11 11L21 21" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            ) : null
                          ) : showClearControl ? (
                            <button
                              type="button"
                              className="scs__close-btn"
                              aria-label={t("Clear search")}
                              onClick={clearSearchInput}
                            >
                              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11 21L21 11M11 11L21 21" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          ) : null}

                          {!isDiscoverMode && !queryText.trim() ? (
                            <button
                              type="button"
                              className="scs__icon-btn"
                              title={imageName ? t("Change image") : t("Search with image")}
                              onClick={openImageUploadModal}
                            >
                              <img src={camiconuploadimage} alt="" aria-hidden />
                            </button>
                          ) : null}

                          <div className="scs__discover-actions">
                            <MicButton
                              value={isDiscoverMode ? intent : queryText}
                              onChange={
                                isDiscoverMode ? handleDiscoverIntentChange : setQueryText
                              }
                              disabled={isLoading}
                              t={t}
                              onStatusChange={handleVoiceStatusChange}
                              onAudioLevels={handleVoiceAudioLevels}
                            />
                            {showDiscoverButton ? (
                              <button
                                type="button"
                                className="scs__discover-btn"
                                onClick={handleDiscoveryClick}
                                disabled={isLoading}
                              >
                                <img src={greyfaroicon} alt="" aria-hidden />
                                <span>{t("Discovery")}</span>
                              </button>
                            ) : null}
                            {isDiscoverMode
                              ? isSearchFieldExpanded && hasSearchInput
                                ? (
                                    <button
                                      type="submit"
                                      className="scs__enter-btn"
                                      aria-label={t("Discover")}
                                      disabled={isLoading}
                                    >
                                      <span aria-hidden />
                                    </button>
                                  )
                                : null
                              : hasSearchInput
                                ? (
                                    <button
                                      type="submit"
                                      className="scs__enter-btn"
                                      aria-label={t("Search")}
                                      disabled={isLoading}
                                    >
                                      <span aria-hidden />
                                    </button>
                                  )
                                : null}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </form>

              </section>
              ) : null}

              {isDiscoverMode && !hasSearched ? (
                <div className="scs__prompt-chips" aria-label={t("Prompt chips")}>
                  {DISCOVER_PROMPT_GROUPS.map((group) => (
                    <button
                      key={group.key}
                      type="button"
                      className={`scs__prompt-chip${
                        activePromptKey === group.key ? " is-active" : ""
                      }`}
                      onClick={() => handlePromptChip(group)}
                      onMouseEnter={() => handleParentChipMouseEnter(group)}
                      onMouseLeave={handleParentChipMouseLeave}
                    >
                      {t(group.label)}
                    </button>
                  ))}
                </div>
              ) : null}

              {isDiscoverMode && !hasSearched && activePromptGroup ? (
                <div className="scs__child-prompts">
                  <div className="scs__child-prompts-header">
                    {t(activePromptGroup.label)}
                  </div>
                  <div className="scs__child-prompts-body">
                    {activePromptGroup.children.map((child) => (
                      <button
                        key={child}
                        type="button"
                        className={`scs__child-prompt-chip${
                          selectedPromptChildren[activePromptGroup.key]?.includes(child)
                            ? " is-active"
                            : ""
                        }`}
                        onClick={() => handleChildPromptChip(activePromptGroup, child)}
                        onMouseEnter={() => handleChildChipMouseEnter(child)}
                        onMouseLeave={handleChildChipMouseLeave}
                      >
                        {t(child)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {!isLoading && hasSearched && weak && !products.length ? (
                <div className="scs__weak">
                  {t("No strong matches — try rephrasing your search.")}
                </div>
              ) : null}

              <section
                className={`scs__results${
                  hasSearched ? " scs__results--active" : ""
                }${isLoading ? " scs__results--loading" : ""}${
                  isOrderOpen ? " scs__results--order" : ""
                }`}
              >
                {isLoading ? (
                  <div className="scs__loading-panel">
                    <div className="scs__loading-head">
                      <span>{t("Showing")}</span>
                      {!isFromPosscreen && !isFromQuickview ? (
                        <label className="scs__instock-only">
                          <input
                            type="checkbox"
                            checked={Boolean(filters.inStock)}
                            onChange={handleInStockOnlyChange}
                            disabled={isLoading}
                          />
                          <span>{t("In stock only")}</span>
                        </label>
                      ) : null}
                    </div>
                    <div className="scs__loading-list">
                      <div className="scs__loading-grid">
                        {Array.from({ length: 16 }).map((_, i) => (
                          <SkeletonCard key={i} />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : isOrderOpen ? (
                  <OrderPanel
                    items={orderItems}
                    t={t}
                    editingItemId={editingOrderItemId}
                    openDropdown={orderOpenDropdown}
                    onToggleDropdown={handleOrderDropdownToggle}
                    onCloseDropdown={() => setOrderOpenDropdown(null)}
                    onClose={() => setIsOrderOpen(false)}
                    onEdit={handleEditOrderItem}
                    onDelete={handleDeleteOrderItem}
                    onUpdateSelections={handleUpdateOrderItem}
                    onConfirmEdit={handleConfirmOrderEdit}
                    onSubmit={handleOrderSubmit}
                    isSubmitting={orderSubmitting}
                    showOrderEdit={!isInventoryPosCart}
                  />
                ) : products.length ? (
                  <div className="scs__results-panel">
                    <div className="scs__results-panel-head">
                      <span>{t("Showing")} ({products.length})</span>
                      {!isFromPosscreen && !isFromQuickview ? (
                        <label className="scs__instock-only">
                          <input
                            type="checkbox"
                            checked={Boolean(filters.inStock)}
                            onChange={handleInStockOnlyChange}
                          />
                          <span>{t("In stock only")}</span>
                        </label>
                      ) : null}
                    </div>
                    <div
                      className="scs__results-panel-list"
                      onScroll={handleResultsListScroll}
                    >
                      <div className="scs__grid">
                        {products.map((product, i) => {
                          const cardKey = getProductCardKey(product, i);
                          return (
                          <ProductCard
                            key={product._id || product.sku || product.stock_id || cardKey}
                            product={product}
                            t={t}
                            index={i}
                            addedCount={addedProductCounts[getProductIdentity(product)] || 0}
                            onConfirmSelection={handleConfirmSelection}
                            onImageClick={openProductGallery}
                            isConfigOpen={activeConfigCardKey === cardKey}
                            onOpenConfig={openProductConfig}
                            onCloseConfig={closeProductConfig}
                            fromPosscreen={isInventoryPosCart}
                            fromQuickview={isFromQuickview}
                            showStockId={Boolean(filters.inStock)}
                          />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : hasSearched ? (
                  <div className="scs__results-panel">
                    <div className="scs__results-panel-head">
                      <span>{t("Showing")} (0)</span>
                      {!isFromPosscreen && !isFromQuickview ? (
                        <label className="scs__instock-only">
                          <input
                            type="checkbox"
                            checked={Boolean(filters.inStock)}
                            onChange={handleInStockOnlyChange}
                          />
                          <span>{t("In stock only")}</span>
                        </label>
                      ) : null}
                    </div>
                    <div className="scs__empty">
                      <h3 className="scs__empty-title">{t("No matches found")}</h3>
                      <p className="scs__empty-sub">
                        {t("Try different words or describe the jewelry with AI.")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="scs__chips" aria-label={t("Search examples")}>
                    {examples.slice(0, 3).map((ex) => (
                      <button
                        key={ex}
                        type="button"
                        className="scs__chip"
                        onClick={() => handleExample(ex)}
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                )}
              </section>
            </main>
          </div>
        </div>
        <div className="main-body-row-right-col">
          <CheckoutBlank />
        </div>
      </div>

      <ImageUploadModal
        open={isImageUploadOpen}
        onClose={closeImageUploadModal}
        onFile={applyImageFile}
        t={t}
      />
      <ProductImageGalleryModal
        open={Boolean(galleryProduct)}
        product={galleryProduct}
        onClose={closeProductGallery}
        t={t}
      />
    </div>
  );
};

export default SmartCatalogSearch;